import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import './TeacherDashboard.css';
import './HomeworkCompletionModal.css';

// Get Supabase URL for constructing storage URLs
const SUPABASE_URL = 'https://gjamvfwvqlizginqdfhd.supabase.co';

const getTodayISODateString = () => {
  const today = new Date();
  today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
  return today.toISOString().split('T')[0];
};

const addDaysToISODateString = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().split('T')[0];
};

function TeacherDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(true);
  const [classNames, setClassNames] = useState({});
  
  // Get active tab from URL
  const getTabFromPath = (pathname) => {
    const path = pathname.split('/').pop();
    const validTabs = ['dashboard', 'schedule', 'attendance', 'records', 'homework', 'exams', 'chat'];
    return validTabs.includes(path) ? path : 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  const [teacherSchedule, setTeacherSchedule] = useState([]);
  const [schedulesBySemester, setSchedulesBySemester] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [schoolTimings, setSchoolTimings] = useState(null);
  const [attendanceClasses, setAttendanceClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [availableSections, setAvailableSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [existingAttendance, setExistingAttendance] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [savedStudentsCount, setSavedStudentsCount] = useState(0);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [homeworkData, setHomeworkData] = useState([]);
  const [homeworkLoading, setHomeworkLoading] = useState(false);
  const [selectedHomeworkDate, setSelectedHomeworkDate] = useState(getTodayISODateString());
  const [editingHomework, setEditingHomework] = useState(null);
  const [editHomeworkData, setEditHomeworkData] = useState({
    title: '',
    description: '',
    due_date: ''
  });
  const [newHomework, setNewHomework] = useState({
    class_key: '',
    class_id: '',
    section_name: '',
    subject: '',
    title: '',
    description: '',
    due_date: '',
    assigned_date: getTodayISODateString()
  });
  const [homeworkFiles, setHomeworkFiles] = useState({}); // Store files per class/subject combo
  const [teacherExams, setTeacherExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStudents, setExamStudents] = useState([]);
  const [examStudentsLoading, setExamStudentsLoading] = useState(false);
  const [marksData, setMarksData] = useState({}); // {studentId: {obtained_marks, total_marks, grade, remarks}}
  const [savingMarks, setSavingMarks] = useState(false);
  const [showMarksSection, setShowMarksSection] = useState(false);
  const [globalTotalMarks, setGlobalTotalMarks] = useState(''); // Total marks for all students
  const [showTotalMarksPopup, setShowTotalMarksPopup] = useState(false);
  const [totalMarksApplied, setTotalMarksApplied] = useState('');
  const [showMarksSavedPopup, setShowMarksSavedPopup] = useState(false);
  const [marksSavedCount, setMarksSavedCount] = useState(0);
  const [chatStudents, setChatStudents] = useState([]);
  const [chatStudentsLoading, setChatStudentsLoading] = useState(false);
  const [selectedChatStudent, setSelectedChatStudent] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [chatGroup, setChatGroup] = useState(null);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [showChatSidebar, setShowChatSidebar] = useState(true);
  const chatMessagesEndRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const marksEntrySectionRef = useRef(null);
  const [unreadCounts, setUnreadCounts] = useState({}); // Track unread counts per student
  // Group chat state
  const [chatGroups, setChatGroups] = useState([]);
  const [selectedChatGroup, setSelectedChatGroup] = useState(null);
  const [groupChatMessages, setGroupChatMessages] = useState([]);
  const [groupChatLoading, setGroupChatLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [chatViewMode, setChatViewMode] = useState('students'); // 'students' or 'groups'
  const [currentGroupChatGroup, setCurrentGroupChatGroup] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Homework completion state
  const [selectedHomeworkForCompletion, setSelectedHomeworkForCompletion] = useState(null);
  const [homeworkStudents, setHomeworkStudents] = useState([]);
  const [homeworkStudentsLoading, setHomeworkStudentsLoading] = useState(false);
  const [homeworkCompletionStatus, setHomeworkCompletionStatus] = useState({}); // {studentId: isCompleted}
  const [showHomeworkCompletionModal, setShowHomeworkCompletionModal] = useState(false);
  const [savingCompletion, setSavingCompletion] = useState(false);
  const [showCompletionSuccessPopup, setShowCompletionSuccessPopup] = useState(false);
  const [completionSuccessMessage, setCompletionSuccessMessage] = useState('');
  const [isCompletionError, setIsCompletionError] = useState(false);
  
  // AI Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const aiChatMessagesEndRef = useRef(null);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    setActiveTab(tab);
  }, [location.pathname]);

  // Scroll to marks entry section when it becomes visible
  useEffect(() => {
    if (showMarksSection && marksEntrySectionRef.current) {
      setTimeout(() => {
        marksEntrySectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 200);
    }
  }, [showMarksSection]);

  // Handle tab change with navigation
  const handleTabChange = (tab) => {
    navigate(`/teacher/${tab}`);
    setActiveTab(tab);
  };

  const fetchTeacherData = async () => {
    try {
      setLoading(true);
      
      // Get teacher data from localStorage first
    const storedTeacherData = localStorage.getItem('teacherData');
      if (!storedTeacherData) {
        console.error('No teacher data in localStorage');
        setLoading(false);
        return;
      }
      
      const parsedData = JSON.parse(storedTeacherData);
      console.log('Teacher data from localStorage:', parsedData);
      console.log('Teacher ID from localStorage:', parsedData.id);
      
      // Try to fetch by email if ID doesn't work
      let teacherData = null;
      let error = null;
      
      if (parsedData.id) {
        const result = await supabase
          .from('teachers')
          .select('*')
          .eq('id', parsedData.id)
          .single();
        teacherData = result.data;
        error = result.error;
      }
      
      // If ID fetch fails, try email
      if (error || !teacherData) {
        console.log('ID fetch failed, trying email:', parsedData.email);
        const emailResult = await supabase
          .from('teachers')
          .select('*')
          .eq('email', parsedData.email)
          .single();
        teacherData = emailResult.data;
        error = emailResult.error;
      }
      
      // If email fetch fails, try auth_user_id
      if (error || !teacherData) {
        console.log('Email fetch failed, trying auth_user_id:', parsedData.auth_user_id);
        const authResult = await supabase
          .from('teachers')
          .select('*')
          .eq('auth_user_id', parsedData.auth_user_id)
          .single();
        teacherData = authResult.data;
        error = authResult.error;
      }
      
      if (error) {
        console.error('Error fetching teacher data:', error);
        // Fallback to localStorage data
      setTeacherData(parsedData);
      } else {
        console.log('Complete teacher data from Supabase:', teacherData);
        setTeacherData(teacherData);
        // Update localStorage with fresh data
        localStorage.setItem('teacherData', JSON.stringify(teacherData));
      }
      
      // Fetch notices for teachers
      fetchTeacherNotices();
    } catch (err) {
      console.error('Error in fetchTeacherData:', err);
      // Fallback to localStorage data
      const storedTeacherData = localStorage.getItem('teacherData');
      if (storedTeacherData) {
        setTeacherData(JSON.parse(storedTeacherData));
      }
    } finally {
    setLoading(false);
    }
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currency) => {
    const currencySymbols = {
      'PKR': '₨',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'AED': 'د.إ',
      'SAR': '﷼',
      'QAR': '﷼',
      'KWD': 'د.ك',
      'BHD': '.د.ب',
      'OMR': '﷼',
      'JOD': 'د.ا',
      'LBP': 'ل.ل',
      'EGP': '£',
      'ILS': '₪',
      'TRY': '₺',
      'IRR': '﷼',
      'IQD': 'ع.د',
      'AFN': '؋',
      'INR': '₹',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'CNY': '¥',
      'SGD': 'S$',
      'MYR': 'RM',
      'THB': '฿',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫',
      'BDT': '৳',
      'LKR': '₨',
      'NPR': '₨',
      'KES': 'KSh',
      'NGN': '₦',
      'ZAR': 'R'
    };
    return currencySymbols[currency] || currency || '₨';
  };

  // Fetch school information when teacherData is loaded - always fetch fresh from database
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (teacherData && teacherData.school_id) {
        // Always fetch fresh school data from Supabase to get the latest school name and currency
        const { data: schoolData, error } = await supabase
          .from('schools')
          .select('id, school_name, currency')
          .eq('id', teacherData.school_id)
          .single();

        if (!error && schoolData) {
          setTeacherData(prev => ({
            ...prev,
            schools: schoolData
          }));
          // Update localStorage with fresh school data
          const updatedTeacherData = {
            ...teacherData,
            schools: schoolData
          };
          localStorage.setItem('teacherData', JSON.stringify(updatedTeacherData));
        }
      }
    };

    fetchSchoolData();
  }, [teacherData?.school_id]);

  const fetchClassNames = async () => {
    try {
      if (!teacherData?.school_id) {
        console.log('No school_id available for fetching class names');
        return;
      }

      console.log('Fetching class names for school_id:', teacherData.school_id);
      const { data: classes, error } = await supabase
        .from('classes')
        .select('id, class_name')
        .eq('school_id', teacherData.school_id);

      if (error) {
        console.error('Error fetching class names:', error);
        return;
      }

      // Create a mapping of class ID to class name
      const classNamesMap = {};
      classes.forEach(cls => {
        classNamesMap[cls.id] = cls.class_name;
      });

      console.log('Class names mapping:', classNamesMap);
      setClassNames(classNamesMap);
    } catch (err) {
      console.error('Error in fetchClassNames:', err);
    }
  };

  const fetchTeacherSchedule = async () => {
    try {
      setScheduleLoading(true);
      console.log('Fetching teacher schedule for teacher_id:', teacherData?.id);
      
      if (!teacherData?.school_id) {
        console.log('No school_id available for fetching schedule');
        return;
      }

      // Fetch ALL schedules for this teacher (all semesters)
      const { data: schedule, error } = await supabase
        .from('class_schedules')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('teacher_id', teacherData.id)
        .order('day', { ascending: true })
        .order('period', { ascending: true });

      if (error) {
        console.error('Error fetching teacher schedule:', error);
        return;
      }

      console.log('Teacher schedule fetched:', schedule);
      console.log('Schedule length:', schedule?.length);
      console.log('All days in fetched schedule:', schedule?.map(s => s.day));
      console.log('Unique days in fetched schedule:', [...new Set(schedule?.map(s => s.day))]);
      
      if (schedule && schedule.length > 0) {
        // Group schedules by semester
        const schedulesGrouped = {};
        
        schedule.forEach(entry => {
          const semester = entry.semester || '1st Semester';
          const semesterName = entry.semester_name || semester;
          
          if (!schedulesGrouped[semester]) {
            schedulesGrouped[semester] = {
              semester: semester,
              semester_name: semesterName,
              schedule: []
            };
          }
          
          schedulesGrouped[semester].schedule.push(entry);
        });
        
        // Sort semesters: "1st Semester" first, then others alphabetically
        const sortedSemesters = Object.keys(schedulesGrouped).sort((a, b) => {
          if (a === '1st Semester') return -1;
          if (b === '1st Semester') return 1;
          return a.localeCompare(b);
        });
        
        const orderedSchedules = {};
        sortedSemesters.forEach(sem => {
          orderedSchedules[sem] = schedulesGrouped[sem];
        });
        
        setSchedulesBySemester(orderedSchedules);
        
        // For backward compatibility, use first semester as default teacherSchedule
        const firstSemester = sortedSemesters[0];
        if (firstSemester) {
          setTeacherSchedule(orderedSchedules[firstSemester].schedule || []);
        } else {
          setTeacherSchedule(schedule || []);
        }
      } else {
        setTeacherSchedule([]);
        setSchedulesBySemester({});
      }
    } catch (err) {
      console.error('Error in fetchTeacherSchedule:', err);
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchSchoolTimings = async () => {
    try {
      console.log('Fetching school timings for school_id:', teacherData?.school_id);
      
      if (!teacherData?.school_id) {
        console.log('No school_id available for fetching school timings');
        return;
      }

      // Align with School's ScheduleView logic:
      // - use school-wide timings (class_id IS NULL)
      // - filter by teacher's branch_id when available
      // - prefer the most recently created timing record
      let query = supabase
        .from('school_timings')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .is('class_id', null);

      // If teacher belongs to a specific branch, use branch-specific timings.
      // Otherwise, fallback to timings where branch_id IS NULL.
      if (teacherData.branch_id) {
        query = query.eq('branch_id', teacherData.branch_id);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching school timings for teacher:', error);
        return;
      }

      if (data && data.length > 0) {
        const timings = data[0];
        console.log('School timings fetched for teacher:', timings);
        setSchoolTimings(timings);
      } else {
        console.log('No school-wide school_timings found for this school_id; falling back to defaults');
        setSchoolTimings(null);
      }
    } catch (err) {
      console.error('Error in fetchSchoolTimings:', err);
    }
  };

  const addMinutes = (timeString, minutes) => {
    // Ensure timeString is in HH:MM format
    let formattedTime = timeString;
    if (!timeString.includes(':')) {
      formattedTime = timeString.padStart(2, '0') + ':00';
    }
    
    const [hours, mins] = formattedTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    // Handle null or undefined
    if (timeString === null || timeString === undefined) return 'N/A';
    
    // Convert to string if it's not already
    const timeStr = String(timeString).trim();
    
    // If empty string, return N/A
    if (!timeStr) return 'N/A';
    
    // Ensure we always show HH:MM format
    if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
      return timeStr;
    }
    
    // If it's missing minutes, add :00
    if (timeStr.match(/^\d{1,2}$/)) {
      return timeStr.padStart(2, '0') + ':00';
    }
    
    return timeStr;
  };

  const calculatePeriodTimings = () => {
    // Use school timings from database if available, otherwise fall back to sensible defaults
    const timingsConfig = schoolTimings || {
      start_time: '08:00',
      period_time: 45,
      break_time: 15,
      breaks: [],
      number_of_periods: 8
    };
    
    const startTime = timingsConfig.start_time || '08:00';
    const periodDuration = timingsConfig.period_time || 45;
    const breakDuration = timingsConfig.break_time || 15;
    const breaks = timingsConfig.breaks || [];
    
    const timings = {};
    let currentTime = startTime;
    
    // Ensure startTime is in HH:MM format
    if (!currentTime.includes(':')) {
      currentTime = currentTime.padStart(2, '0') + ':00';
    }
    
    for (let period = 1; period <= (timingsConfig.number_of_periods || 8); period++) {
      const startTimeFormatted = formatTime(currentTime);
      const endTime = addMinutes(currentTime, periodDuration);
      const endTimeFormatted = formatTime(endTime);
      
      timings[period] = {
        start: startTimeFormatted,
        end: endTimeFormatted,
        duration: periodDuration
      };
      
      // Check if there's a break that starts right after this period ends
      const breakAfterPeriod = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime);
        const periodEnd = formatTime(endTime);
        return breakStart === periodEnd;
      });
      
      if (breakAfterPeriod) {
        // If there's a break right after this period, next period starts after break ends
        currentTime = breakAfterPeriod.endTime;
        
        // Ensure break end time is in HH:MM format
        if (!currentTime.includes(':')) {
          currentTime = currentTime.padStart(2, '0') + ':00';
        }
      } else {
        // No break after this period, so next period starts immediately after this period ends
        currentTime = endTime;
      }
    }
    
    return timings;
  };

  const calculatePeriodTime = (period) => {
    const periodTimings = calculatePeriodTimings();
    const periodTiming = periodTimings[period];
    
    if (!periodTiming) return 'Time TBA';
    
    return `${periodTiming.start} - ${periodTiming.end}`;
  };

  const fetchAttendanceClasses = async () => {
    try {
      console.log('Fetching attendance classes for teacher_id:', teacherData?.id);
      
      if (!teacherData?.school_id) {
        console.log('No school_id available for fetching attendance classes');
        return;
      }

      const { data: assignedClasses, error: classesError } = await supabase
        .from('classes')
        .select('id, class_name, sections, class_teacher_id, assistant_teacher_id')
        .eq('school_id', teacherData.school_id)
        .eq('is_active', true)
        .or(`class_teacher_id.eq.${teacherData.id},assistant_teacher_id.eq.${teacherData.id}`)
        .order('class_name', { ascending: true });

      if (classesError) {
        console.error('Error fetching attendance classes:', classesError);
        setAttendanceClasses([]);
        return;
      }

      const classesArray = (assignedClasses || []).map(classItem => {
        let sections = classItem.sections || [];

        if (typeof sections === 'string') {
          try {
            sections = JSON.parse(sections);
          } catch (error) {
            sections = [sections];
          }
        }

        const cleanSections = Array.isArray(sections)
          ? sections.map(section => String(section).trim()).filter(Boolean)
          : [];

        return {
          class_id: classItem.id,
          class_name: classItem.class_name || `Class ${classItem.id}`,
          class_teacher_id: classItem.class_teacher_id,
          assistant_teacher_id: classItem.assistant_teacher_id,
          sections: [...new Set(cleanSections)]
        };
      });

      console.log('Class teacher/assistant attendance classes:', assignedClasses);
      console.log('Final classes array:', classesArray);
      setAttendanceClasses(classesArray);
    } catch (err) {
      console.error('Error in fetchAttendanceClasses:', err);
    }
  };

  const canMarkAttendanceForSelectedClass = () => {
    if (!selectedClass || !teacherData?.id) return false;

    const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
    const classInfo = attendanceClasses.find(cls => parseInt(cls.class_id) === normalizedClassId);

    if (!classInfo) return false;

    return (
      parseInt(classInfo.class_teacher_id) === parseInt(teacherData.id) ||
      parseInt(classInfo.assistant_teacher_id) === parseInt(teacherData.id)
    );
  };

  const ensureCanMarkAttendance = () => {
    if (canMarkAttendanceForSelectedClass()) return true;

    alert('Only the class teacher or assistant teacher can mark attendance for this class.');
    return false;
  };

  // Helper function to extract base class name (removing section info)
  const getClassDisplayName = (className) => {
    if (!className) return '';
    // Split by " - " and take the first part (base class name)
    return className.split(' - ')[0].trim();
  };

  const getStudentDisplayId = (student, fallbackId = 'N/A') => {
    return student?.student_id_no || student?.student_id || fallbackId;
  };

  const fetchSectionsForClass = async () => {
    if (!selectedClass || !teacherData?.school_id) {
      setAvailableSections([]);
      return;
    }

    try {
      setLoadingSections(true);
      const classId = parseInt(selectedClass);
      const selectedClassData = attendanceClasses.find(cls => parseInt(cls.class_id) === classId);

      if (!selectedClassData) {
        setAvailableSections([]);
        return;
      }

      const sectionsArray = (selectedClassData.sections || [])
        .filter(s => s && s.trim() !== '')
        .sort()
        .map(section => ({
          id: section,
          section_name: section
        }));

      console.log('Available sections for class:', sectionsArray);
      setAvailableSections(sectionsArray);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setAvailableSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection) {
      setStudents([]);
      return;
    }

    try {
      setStudentsLoading(true);
      console.log('Fetching students for class:', selectedClass, 'section:', selectedSection);

      // Normalize class_id and section_id for query
      const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
      const normalizedSectionId = String(selectedSection).trim();

      const { data: studentsData, error } = await supabase
        .from('students')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', normalizedClassId)
        .eq('section_id', normalizedSectionId)
        .order('first_name', { ascending: true });

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      console.log('Students fetched:', studentsData);
      const fetchedStudents = studentsData || [];
      setStudents(fetchedStudents);
      
      // Set default attendance as "present" for all students
      const defaultAttendance = {};
      fetchedStudents.forEach(student => {
        defaultAttendance[student.id] = 'present';
      });
      setAttendanceData(defaultAttendance);
    } catch (err) {
      console.error('Error in fetchStudents:', err);
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchExistingAttendance = async () => {
    if (!selectedClass || !selectedSection || !teacherData?.school_id) {
      return;
    }

    try {
      const attendanceDate = selectedAttendanceDate || new Date().toISOString().split('T')[0];
      console.log('Fetching existing attendance for:', {
        classId: selectedClass,
        sectionId: selectedSection,
        schoolId: teacherData.school_id,
        date: attendanceDate
      });

      // Normalize class_id and section_id for query
      const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
      const normalizedSectionId = String(selectedSection).trim();

      const { data: attendanceRecords, error } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', normalizedClassId)
        .eq('section_id', normalizedSectionId)
        .eq('attendance_date', attendanceDate);

      if (error) {
        console.error('Error fetching existing attendance:', error);
        return;
      }

      console.log('Existing attendance records:', attendanceRecords);

      // Convert array to object with student_id as key
      const attendanceMap = {};
      attendanceRecords.forEach(record => {
        attendanceMap[record.student_id] = record.status;
      });

      // Merge existing attendance with default "present" for students without records
      const mergedAttendance = { ...attendanceData }; // Start with current attendance data (which has defaults)
      attendanceRecords.forEach(record => {
        mergedAttendance[record.student_id] = record.status; // Override with existing records
      });
      
      // Ensure all current students have attendance (default to "present" if not set)
      students.forEach(student => {
        if (!mergedAttendance[student.id]) {
          mergedAttendance[student.id] = 'present';
        }
      });

      setExistingAttendance(attendanceMap);
      setAttendanceData(mergedAttendance); // Set merged attendance data
      
    } catch (err) {
      console.error('Error in fetchExistingAttendance:', err);
    }
  };

  const markAttendance = async (studentId, status) => {
    if (!selectedClass || !selectedSection) {
      alert('Please select both class and section first');
      return;
    }

    if (!ensureCanMarkAttendance()) {
      return;
    }

    try {
      setAttendanceLoading(true);
      const attendanceDate = selectedAttendanceDate || new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      console.log('Marking attendance:', {
        studentId,
        status,
        classId: selectedClass,
        sectionId: selectedSection,
        attendanceDate: attendanceDate,
        teacherId: teacherData.id,
        schoolId: teacherData.school_id
      });

      // Check if attendance record already exists for the selected date
      const { data: existingAttendance, error: checkError } = await supabase
        .from('attendance')
        .select('*')
        .eq('student_id', studentId)
        .eq('attendance_date', attendanceDate)
        .eq('school_id', teacherData.school_id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking existing attendance:', checkError);
        return;
      }

      if (existingAttendance) {
        // Update existing attendance record
        const { error: updateError } = await supabase
          .from('attendance')
          .update({
            status: status,
            updated_at: new Date().toISOString(),
            teacher_id: teacherData.id
          })
          .eq('id', existingAttendance.id);

        if (updateError) {
          console.error('Error updating attendance:', updateError);
          alert('Failed to update attendance. Please try again.');
          return;
        }
      } else {
        // Normalize class_id and section_id for insert
        const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
        const normalizedSectionId = String(selectedSection).trim();
        
        // Create new attendance record
        const { error: insertError } = await supabase
          .from('attendance')
          .insert({
            student_id: studentId,
            class_id: normalizedClassId,
            section_id: normalizedSectionId,
            school_id: teacherData.school_id,
            teacher_id: teacherData.id,
            attendance_date: attendanceDate,
            status: status,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating attendance:', insertError);
          alert('Failed to mark attendance. Please try again.');
          return;
        }
      }

      // Update local state
      setAttendanceData(prev => ({
        ...prev,
        [studentId]: status
      }));

      // Update existing attendance state as well
      setExistingAttendance(prev => ({
        ...prev,
        [studentId]: status
      }));

      console.log(`Attendance marked: Student ${studentId} - ${status}`);
      
    } catch (err) {
      console.error('Error in markAttendance:', err);
      alert('An error occurred while marking attendance. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const markAllPresent = () => {
    if (students.length === 0) return;
    if (!ensureCanMarkAttendance()) return;

    const allPresent = {};
    students.forEach(student => {
      allPresent[student.id] = 'present';
    });
    setAttendanceData(allPresent);
  };

  const markAllAbsent = () => {
    if (students.length === 0) return;
    if (!ensureCanMarkAttendance()) return;

    const allAbsent = {};
    students.forEach(student => {
      allAbsent[student.id] = 'absent';
    });
    setAttendanceData(allAbsent);
  };

  const saveAllAttendance = async () => {
    if (!selectedClass || !selectedSection) {
      alert('Please select both class and section first');
      return;
    }

    if (students.length === 0) {
      alert('No students to save attendance for');
      return;
    }

    if (!ensureCanMarkAttendance()) {
      return;
    }

    try {
      setAttendanceLoading(true);
      const attendanceDate = selectedAttendanceDate || new Date().toISOString().split('T')[0];
      const normalizedClassId = typeof selectedClass === 'string' ? parseInt(selectedClass, 10) : selectedClass;
      const normalizedSectionId = String(selectedSection).trim();

      // Prepare all attendance records using CURRENT attendanceData state
      // This ensures any changes made by the user are captured
      const attendanceRecords = students.map(student => {
        // Get the current status from attendanceData state (this reflects any changes made)
        const currentStatus = attendanceData[student.id] || 'present';
        return {
          student_id: student.id,
          class_id: normalizedClassId,
          section_id: normalizedSectionId,
          school_id: teacherData.school_id,
          teacher_id: teacherData.id,
          attendance_date: attendanceDate,
          status: currentStatus, // Use current status from state
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      });

      // Check existing attendance records for the selected date
      const { data: existingRecords, error: fetchError } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', normalizedClassId)
        .eq('section_id', normalizedSectionId)
        .eq('attendance_date', attendanceDate);

      if (fetchError) {
        console.error('Error fetching existing attendance:', fetchError);
        alert('Failed to fetch existing attendance. Please try again.');
        return;
      }

      // Create a map of existing records by student_id
      const existingMap = {};
      existingRecords?.forEach(record => {
        existingMap[record.student_id] = record;
      });

      // Separate records into updates and inserts
      const recordsToUpdate = [];
      const recordsToInsert = [];

      attendanceRecords.forEach(record => {
        const existingRecord = existingMap[record.student_id];
        if (existingRecord) {
          // Check if status has changed - always update to ensure latest status is saved
          const currentStatus = attendanceData[record.student_id] || 'present';
          recordsToUpdate.push({
            id: existingRecord.id,
            student_id: record.student_id,
            status: currentStatus, // Use current status from attendanceData state
            updated_at: new Date().toISOString(),
            teacher_id: teacherData.id
          });
        } else {
          // Insert new record
          recordsToInsert.push(record);
        }
      });

      // Perform updates - update all existing records with current status
      if (recordsToUpdate.length > 0) {
        for (const record of recordsToUpdate) {
          const { error: updateError } = await supabase
            .from('attendance')
            .update({
              status: record.status, // This will be the current status from attendanceData
              updated_at: record.updated_at,
              teacher_id: record.teacher_id
            })
            .eq('id', record.id);

          if (updateError) {
            console.error('Error updating attendance:', updateError);
            throw updateError;
          }
        }
        console.log(`Updated ${recordsToUpdate.length} existing attendance records`);
      }

      // Perform inserts
      if (recordsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('attendance')
          .insert(recordsToInsert);

        if (insertError) {
          console.error('Error inserting attendance:', insertError);
          throw insertError;
        }
      }

      // Update existing attendance state
      const updatedAttendanceMap = {};
      attendanceRecords.forEach(record => {
        updatedAttendanceMap[record.student_id] = record.status;
      });
      setExistingAttendance(updatedAttendanceMap);

      // Show success popup
      setSavedStudentsCount(attendanceRecords.length);
      setShowSuccessPopup(true);
      console.log(`Attendance saved for ${attendanceRecords.length} students`);

      // Auto-hide popup after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);

    } catch (err) {
      console.error('Error in saveAllAttendance:', err);
      alert('An error occurred while saving attendance. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const getTodayDateString = () => {
    const dateToFormat = selectedAttendanceDate ? new Date(selectedAttendanceDate) : new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return dateToFormat.toLocaleDateString('en-US', options);
  };

  const fetchAttendanceRecords = async () => {
    if (!selectedClass || !selectedSection || !teacherData?.school_id) {
      return;
    }

    setRecordsLoading(true);
    
    try {
      // Calculate start and end dates for the selected month/year
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];
      
      console.log('Fetching attendance records for:', {
        classId: selectedClass,
        sectionId: selectedSection,
        schoolId: teacherData.school_id,
        startDate,
        endDate,
        month: selectedMonth + 1,
        year: selectedYear
      });

      // First, let's try a simple query to see all attendance records
      const { data: allRecords, error: allError } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_id', teacherData.school_id);
      
      console.log('All attendance records for school:', allRecords);
      console.log('All records error:', allError);
      
      // Let's also check if there are ANY attendance records at all
      const { data: anyRecords, error: anyError } = await supabase
        .from('attendance')
        .select('*')
        .limit(10);
      
      console.log('Any attendance records in database:', anyRecords);
      console.log('Any records error:', anyError);
      
      // Let's also check what data types we're working with
      console.log('Selected class type:', typeof selectedClass, selectedClass);
      console.log('Selected section type:', typeof selectedSection, selectedSection);
      console.log('School ID type:', typeof teacherData.school_id, teacherData.school_id);
      
      // Fetch attendance records (simplified query without join)
      console.log('Fetching attendance records...');
      const { data: records, error } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', selectedClass)
        .eq('section_id', selectedSection)
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate)
        .order('attendance_date', { ascending: true });
      
      console.log('Attendance records query result:', records);
      console.log('Attendance records query error:', error);
      
      // Debug the records to see if student data is included
      if (records && records.length > 0) {
        console.log('Sample record with student data:', records[0]);
        console.log('Student data in first record:', records[0].students);
      }

      if (error) {
        console.error('Error fetching attendance records:', error);
        return;
      }

      // Always fetch student data separately to ensure we have names
      if (records && records.length > 0) {
        console.log('=== DEBUGGING STUDENT DATA FETCH ===');
        console.log('Records found:', records.length);
        console.log('Sample record before student fetch:', records[0]);
        
        const studentIds = [...new Set(records.map(record => record.student_id))];
        console.log('Unique student IDs from records:', studentIds);
        console.log('School ID for student query:', teacherData.school_id);
        
        // First, let's try to fetch all students for this school to see what data we have
        console.log('Fetching all students for school to debug...');
        const { data: allStudents, error: allStudentsError } = await supabase
          .from('students')
          .select('*')
          .eq('school_id', teacherData.school_id);
        
        console.log('All students for school:', allStudents);
        console.log('All students error:', allStudentsError);
        
        // Now fetch the specific students we need - use the same approach as Attendance tab
        const { data: studentsData, error: studentsError } = await supabase
          .from('students')
          .select('*')
          .eq('school_id', teacherData.school_id)
          .eq('class_id', selectedClass)
          .eq('section_id', selectedSection);
        
        console.log('Students query result:', studentsData);
        console.log('Students query error:', studentsError);
        
        if (studentsError) {
          console.error('Error fetching students:', studentsError);
        } else {
          console.log('Fetched students data:', studentsData);
          console.log('Number of students found:', studentsData?.length || 0);
          
          // Create a map of student data - try both id and student_id fields
          const studentsMap = {};
          studentsData?.forEach(student => {
            console.log('Student data:', student);
            console.log('Student id:', student.id, 'student_id:', student.student_id);
            console.log('Student name:', student.first_name, student.last_name);
            
            // Map by both possible ID fields
            if (student.id) studentsMap[student.id] = student;
            if (student.student_id) studentsMap[student.student_id] = student;
          });
          
          console.log('Students map created:', studentsMap);
          
          // Add student data to records
          records.forEach(record => {
            console.log('Record student_id:', record.student_id, 'type:', typeof record.student_id);
            const studentData = studentsMap[record.student_id] || studentsMap[parseInt(record.student_id)];
            console.log(`Adding student data to record ${record.student_id}:`, studentData);
            record.students = studentData;
          });
          
          console.log('Sample record after student data addition:', records[0]);
          console.log('Student data in first record:', records[0].students);
        }
      }

      console.log('Attendance records fetched:', records);
      console.log('Records count:', records?.length || 0);
      console.log('Query parameters:', {
        school_id: teacherData.school_id,
        class_id: selectedClass,
        section_id: selectedSection,
        startDate,
        endDate
      });
      
      setAttendanceRecords(records || []);
      
    } catch (err) {
      console.error('Error in fetchAttendanceRecords:', err);
    } finally {
      setRecordsLoading(false);
    }
  };

  const checkForNewDay = () => {
    const today = new Date().toISOString().split('T')[0];
    const lastAttendanceDate = localStorage.getItem('lastAttendanceDate');
    
    if (lastAttendanceDate !== today) {
      // New day - clear attendance data
      setAttendanceData({});
      setExistingAttendance({});
      localStorage.setItem('lastAttendanceDate', today);
      console.log('New day detected - cleared attendance data');
    }
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const groupRecordsByDate = () => {
    const grouped = {};
    attendanceRecords.forEach(record => {
      const date = record.attendance_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(record);
    });
    return grouped;
  };

  const getAttendanceStats = () => {
    const grouped = groupRecordsByDate();
    const totalDays = Object.keys(grouped).length;
    const totalRecords = attendanceRecords.length;
    
    const presentCount = attendanceRecords.filter(r => r.status === 'present').length;
    const absentCount = attendanceRecords.filter(r => r.status === 'absent').length;
    
    return {
      totalDays,
      totalRecords,
      presentCount,
      absentCount,
      attendanceRate: totalRecords > 0 ? ((presentCount / totalRecords) * 100).toFixed(1) : 0
    };
  };

  // Helper function to get correct URL for attachment
  const getAttachmentUrl = async (attachment) => {
    if (!attachment || !attachment.url) return null;
    
    // If URL already exists and is valid, return it
    if (attachment.url && attachment.url.startsWith('http')) {
      // Check if URL is accessible, if not try to get signed URL
      try {
        const response = await fetch(attachment.url, { method: 'HEAD' });
        if (response.ok) {
          return attachment.url;
        }
      } catch (e) {
        console.warn('URL not accessible, trying to get signed URL');
      }
    }
    
    // If we have a path, try to get URL from storage
    if (attachment.path) {
      const bucketName = attachment.bucket || 'student-images';
      try {
        // Try public URL first
        const { data: { publicUrl } } = supabase.storage
          .from(bucketName)
          .getPublicUrl(attachment.path);
        
        // Verify URL is accessible
        try {
          const response = await fetch(publicUrl, { method: 'HEAD' });
          if (response.ok) {
            return publicUrl;
          }
        } catch (e) {
          console.warn('Public URL not accessible, trying signed URL');
        }
        
        // Try signed URL as fallback
        const { data: signedData, error: signedError } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(attachment.path, 3600);
        
        if (!signedError && signedData) {
          return signedData.signedUrl;
        }
      } catch (error) {
        console.error('Error getting URL from storage:', error);
      }
    }
    
    return attachment.url;
  };

  const fetchHomeworkData = async () => {
    if (!teacherData) return;
    
    try {
      setHomeworkLoading(true);
      console.log('=== FETCHING HOMEWORK DATA ===');
      console.log('Teacher data:', teacherData);

      // Fetch homework records for this teacher
      const { data: homework, error: homeworkError } = await supabase
        .from('homework')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('teacher_id', teacherData.id)
        .order('assigned_date', { ascending: false });

      if (homeworkError) {
        console.error('Error fetching homework:', homeworkError);
        return;
      }

      console.log('Homework data fetched:', homework);
      setHomeworkData(homework || []);

    } catch (error) {
      console.error('Error fetching homework data:', error);
    } finally {
      setHomeworkLoading(false);
    }
  };

  const fetchTeacherExams = async () => {
    if (!teacherData || !teacherSchedule || teacherSchedule.length === 0) {
      console.log('Cannot fetch exams: missing teacher data or schedule');
      return;
    }

    try {
      setExamsLoading(true);
      console.log('=== FETCHING TEACHER EXAMS ===');
      console.log('Teacher schedule:', teacherSchedule);

      // Build a map of teacher's subjects by class and section
      const teacherSubjectsMap = {};
      teacherSchedule.forEach(schedule => {
        if (schedule.class_id && schedule.subject_name) {
          const classId = typeof schedule.class_id === 'string' ? parseInt(schedule.class_id, 10) : schedule.class_id;
          const sectionName = schedule.section_name || schedule.section || schedule.section_id || 'all';
          const key = `${classId}_${sectionName}`;
          
          if (!teacherSubjectsMap[key]) {
            teacherSubjectsMap[key] = {
              class_id: classId,
              section_name: sectionName,
              subjects: []
            };
          }
          
          if (!teacherSubjectsMap[key].subjects.includes(schedule.subject_name)) {
            teacherSubjectsMap[key].subjects.push(schedule.subject_name);
          }
        }
      });

      console.log('Teacher subjects map:', teacherSubjectsMap);

      // Get teacher's assigned class IDs
      const teacherClassIds = teacherData.class_ids || [];
      console.log('Teacher class IDs:', teacherClassIds);

      // Fetch all exams for the school
      const { data: allExams, error } = await supabase
        .from('exam_schedule')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('is_active', true)
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching exams:', error);
        setTeacherExams([]);
        return;
      }

      console.log('All exams fetched:', allExams);

      // Filter exams that match teacher's classes and subjects
      const filteredExams = [];
      
      allExams.forEach(exam => {
        const examClassId = typeof exam.class_id === 'string' ? parseInt(exam.class_id, 10) : exam.class_id;
        
        // Check if exam is for one of teacher's classes
        if (!teacherClassIds.includes(examClassId)) {
          return;
        }

        // Check section match
        const teacherSections = teacherData.class_sections && teacherData.class_sections[examClassId] 
          ? teacherData.class_sections[examClassId] 
          : [];
        
        // If exam has a specific section, teacher must teach that section
        // If exam has no section (null), it's for all sections, so teacher should see it
        if (exam.section_name && Array.isArray(teacherSections) && teacherSections.length > 0) {
          if (!teacherSections.includes(exam.section_name)) {
            return; // Teacher doesn't teach this section
          }
        }

        // Collect all subjects teacher teaches for this class
        // Check both specific section and all sections
        const allTeacherSubjects = new Set();
        
        // Get subjects for specific section if exam has one
        if (exam.section_name) {
          const sectionKey = `${examClassId}_${exam.section_name}`;
          const sectionSubjects = teacherSubjectsMap[sectionKey]?.subjects || [];
          sectionSubjects.forEach(subj => allTeacherSubjects.add(subj));
        }
        
        // Also get subjects from all sections for this class (in case teacher teaches multiple sections)
        Object.keys(teacherSubjectsMap).forEach(key => {
          if (key.startsWith(`${examClassId}_`)) {
            const subjects = teacherSubjectsMap[key]?.subjects || [];
            subjects.forEach(subj => allTeacherSubjects.add(subj));
          }
        });

        if (allTeacherSubjects.size === 0) {
          return; // Teacher doesn't teach any subjects for this class
        }

        // Filter exam subjects to only show teacher's subjects
        const examSubjects = exam.subjects || [];
        const relevantSubjects = examSubjects.filter(subject => {
          if (typeof subject === 'object' && subject.name) {
            return allTeacherSubjects.has(subject.name);
          }
          return false;
        });

        // Only include exam if it has at least one relevant subject
        if (relevantSubjects.length > 0) {
          filteredExams.push({
            ...exam,
            relevantSubjects: relevantSubjects // Only show teacher's subjects
          });
        }
      });

      console.log('Filtered exams for teacher:', filteredExams);
      setTeacherExams(filteredExams);

    } catch (error) {
      console.error('Error fetching teacher exams:', error);
      setTeacherExams([]);
    } finally {
      setExamsLoading(false);
    }
  };

  const handleAddMarks = async (exam, subject) => {
    setSelectedExam({ exam, subject });
    setShowMarksSection(true);
    await fetchExamStudents(exam, subject);
    
    // Scroll to marks entry section after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (marksEntrySectionRef.current) {
        marksEntrySectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const fetchExamStudents = async (exam, subject) => {
    if (!teacherData || !exam || !subject) return;

    try {
      setExamStudentsLoading(true);
      console.log('Fetching students for exam:', exam.id, 'subject:', subject.name);

      // Fetch students for the exam's class and section
      let query = supabase
        .from('students')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', exam.class_id)
        .eq('is_active', true);

      // If exam has a specific section, filter by section
      if (exam.section_name) {
        query = query.eq('section_id', exam.section_name);
      }

      const { data: students, error } = await query.order('first_name');

      if (error) {
        console.error('Error fetching students:', error);
        setExamStudents([]);
        return;
      }

      console.log('Fetched students:', students);
      setExamStudents(students || []);

      // Fetch existing marks for these students
      await fetchExistingMarks(exam.id, subject.name, students || []);

    } catch (error) {
      console.error('Error fetching exam students:', error);
      setExamStudents([]);
    } finally {
      setExamStudentsLoading(false);
    }
  };

  const fetchExistingMarks = async (examId, subjectName, studentsList) => {
    if (!teacherData || !examId || !subjectName) return;

    try {
      const studentIds = studentsList.map(s => s.id);
      
      const { data: existingMarks, error } = await supabase
        .from('exam_marks')
        .select('*')
        .eq('exam_schedule_id', examId)
        .eq('subject_name', subjectName)
        .eq('teacher_id', teacherData.id)
        .eq('is_active', true)
        .in('student_id', studentIds);

      if (error) {
        console.error('Error fetching existing marks:', error);
        return;
      }

      console.log('Existing marks:', existingMarks);

      // Initialize marks data with existing marks
      const marks = {};
      let commonTotalMarks = null;
      
      studentsList.forEach(student => {
        const existingMark = existingMarks.find(m => m.student_id === student.id);
        if (existingMark) {
          marks[student.id] = {
            obtained_marks: existingMark.obtained_marks || '',
            total_marks: existingMark.total_marks || '',
            grade: existingMark.grade || '',
            remarks: existingMark.remarks || ''
          };
          // Check if all students have the same total marks
          if (existingMark.total_marks) {
            if (commonTotalMarks === null) {
              commonTotalMarks = existingMark.total_marks;
            } else if (commonTotalMarks !== existingMark.total_marks) {
              commonTotalMarks = null; // Different totals, can't set global
            }
          }
        } else {
          marks[student.id] = {
            obtained_marks: '',
            total_marks: '',
            grade: '',
            remarks: ''
          };
        }
      });

      setMarksData(marks);
      
      // Set global total marks if all students have the same total
      if (commonTotalMarks !== null) {
        setGlobalTotalMarks(String(commonTotalMarks));
      }

    } catch (error) {
      console.error('Error fetching existing marks:', error);
    }
  };

  const handleMarksChange = (studentId, field, value) => {
    setMarksData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const handleSaveMarks = async () => {
    if (!selectedExam || !teacherData) return;

    const { exam, subject } = selectedExam;

    // Validate that at least marks or grade is provided for each student
    const hasData = Object.keys(marksData).some(studentId => {
      const data = marksData[studentId];
      const totalMarks = data.total_marks || (globalTotalMarks ? globalTotalMarks : '');
      return (data.obtained_marks && totalMarks) || (data.grade && data.grade.trim() !== '');
    });

    if (!hasData) {
      alert('Please add at least marks (obtained + total) or grade for at least one student.');
      return;
    }

    try {
      setSavingMarks(true);
      console.log('Saving marks for exam:', exam.id, 'subject:', subject.name);

      const marksToSave = [];
      const marksToUpdate = [];

      // First, fetch all existing marks for this exam and subject
      const studentIds = examStudents.map(s => s.id);
      const { data: existingMarks } = await supabase
        .from('exam_marks')
        .select('id, student_id')
        .eq('exam_schedule_id', exam.id)
        .eq('subject_name', subject.name)
        .eq('teacher_id', teacherData.id)
        .in('student_id', studentIds);

      const existingMarkIds = new Map();
      if (existingMarks) {
        existingMarks.forEach(mark => {
          existingMarkIds.set(mark.student_id, mark.id);
        });
      }

      // Prepare marks for insert/update
      Object.keys(marksData).forEach(studentId => {
        const data = marksData[studentId];
        // Use global total marks if individual total marks is not set
        const totalMarks = data.total_marks || (globalTotalMarks ? globalTotalMarks : '');
        const hasMarks = data.obtained_marks && totalMarks;
        const hasGrade = data.grade && data.grade.trim() !== '';

        // Only save if at least marks or grade is provided
        if (hasMarks || hasGrade) {
          // Validate and parse numeric values
          let obtainedMarksValue = null;
          let totalMarksValue = null;
          
          // Parse obtained marks
          if (data.obtained_marks && data.obtained_marks.toString().trim() !== '') {
            const parsed = parseFloat(data.obtained_marks);
            if (!isNaN(parsed) && parsed >= 0 && parsed <= 99999999.99) {
              obtainedMarksValue = Math.round(parsed * 100) / 100; // Round to 2 decimal places
            } else {
              console.warn(`Invalid obtained marks for student ${studentId}:`, data.obtained_marks);
              return; // Skip this student
            }
          }
          
          // Parse total marks (check both individual and global)
          const totalMarksInput = data.total_marks || globalTotalMarks;
          if (totalMarksInput && totalMarksInput.toString().trim() !== '') {
            const parsed = parseFloat(totalMarksInput);
            if (!isNaN(parsed) && parsed > 0 && parsed <= 99999999.99) {
              totalMarksValue = Math.round(parsed * 100) / 100; // Round to 2 decimal places
            } else {
              console.warn(`Invalid total marks for student ${studentId}:`, totalMarksInput);
              return; // Skip this student
            }
          }

          const markData = {
            school_id: teacherData.school_id,
            exam_schedule_id: exam.id,
            student_id: parseInt(studentId),
            teacher_id: teacherData.id,
            subject_name: subject.name,
            class_id: exam.class_id,
            section_name: exam.section_name || null,
            obtained_marks: obtainedMarksValue,
            total_marks: totalMarksValue,
            grade: hasGrade ? data.grade.trim().toUpperCase() : null,
            remarks: (data.remarks && data.remarks.trim()) || null,
            is_active: true,
            created_by: teacherData.id,
            updated_by: teacherData.id
          };

          // Validate required fields
          if (!markData.school_id || !markData.exam_schedule_id || !markData.student_id || !markData.teacher_id || !markData.subject_name) {
            console.error('Missing required fields for mark:', markData);
            return; // Skip this student
          }

          // Check if mark already exists
          const existingId = existingMarkIds.get(parseInt(studentId));
          if (existingId) {
            marksToUpdate.push({ id: existingId, ...markData });
          } else {
            marksToSave.push(markData);
          }
        }
      });

      console.log('Marks to save:', marksToSave.length);
      console.log('Marks to update:', marksToUpdate.length);

      // Insert new marks
      if (marksToSave.length > 0) {
        console.log('Inserting marks:', marksToSave);
        const { data: insertedData, error: insertError } = await supabase
          .from('exam_marks')
          .insert(marksToSave)
          .select();

        if (insertError) {
          console.error('Error inserting marks:', insertError);
          console.error('Error details:', JSON.stringify(insertError, null, 2));
          alert(`Error saving marks: ${insertError.message || insertError.details || 'Unknown error'}. Please check the console for details.`);
          setSavingMarks(false);
          return;
        }
        console.log('Successfully inserted marks:', insertedData);
      }

      // Update existing marks
      if (marksToUpdate.length > 0) {
        for (const mark of marksToUpdate) {
          const { id, ...updateData } = mark;
          console.log('Updating mark:', id, updateData);
          const { data: updatedData, error: updateError } = await supabase
            .from('exam_marks')
            .update(updateData)
            .eq('id', id)
            .select();

          if (updateError) {
            console.error('Error updating marks:', updateError);
            console.error('Error details:', JSON.stringify(updateError, null, 2));
            alert(`Error updating marks: ${updateError.message || updateError.details || 'Unknown error'}. Please check the console for details.`);
            setSavingMarks(false);
            return;
          }
          console.log('Successfully updated mark:', updatedData);
        }
      }

      const totalSaved = marksToSave.length + marksToUpdate.length;
      if (totalSaved > 0) {
        setMarksSavedCount(totalSaved);
        setShowMarksSavedPopup(true);
        // Auto-hide after 3 seconds
        setTimeout(() => {
          setShowMarksSavedPopup(false);
        }, 3000);
        // Close the marks section after a short delay
        setTimeout(() => {
          setShowMarksSection(false);
          setSelectedExam(null);
          setMarksData({});
          setGlobalTotalMarks('');
        }, 500);
      } else {
        alert('No marks to save. Please add marks or grades for at least one student.');
      }

    } catch (error) {
      console.error('Error saving marks:', error);
      console.error('Error stack:', error.stack);
      alert(`Error saving marks: ${error.message || 'Unknown error'}. Please check the console for details.`);
    } finally {
      setSavingMarks(false);
    }
  };

  const handleAddHomeworkForClass = async (e, homeworkData) => {
    e.preventDefault();
    
    if (!teacherData) return;

    const today = getTodayISODateString();
    if (!homeworkData.assigned_date || homeworkData.assigned_date < today) {
      alert('Homework date must be today or a future date.');
      return;
    }

    if (!homeworkData.due_date || homeworkData.due_date < homeworkData.assigned_date) {
      alert('Due date must be the same as or after the homework date.');
      return;
    }

    if (!homeworkData.class_id || !homeworkData.section_name || !homeworkData.subject) {
      alert('Please select a class before adding homework.');
      return;
    }

    if (!homeworkData.description?.trim()) {
      alert('Please write homework details.');
      return;
    }

    try {
      console.log('Adding homework:', homeworkData);

      // Get file for this homework (if any)
      const fileKey = `${homeworkData.class_id}-${homeworkData.section_name}-${homeworkData.subject}`;
      const file = homeworkFiles[fileKey];
      let attachments = null;

      // Upload file if provided
      if (file) {
        try {
          const fileExt = file.name.split('.').pop();
          const fileName = `homework-${teacherData.school_id}-${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
          const filePath = `homework/${teacherData.school_id}/${fileName}`;

          // Upload to Supabase storage
          // Try student-images bucket first, fallback to other buckets if needed
          let bucketName = 'student-images';
          let uploadError = null;
          let uploadResult = null;

          // Try uploading to student-images bucket
          uploadResult = await supabase.storage
            .from(bucketName)
            .upload(filePath, file);
          
          uploadError = uploadResult.error;

          // If student-images bucket doesn't exist, try alternative buckets
          if (uploadError && uploadError.message && uploadError.message.includes('Bucket not found')) {
            console.warn('student-images bucket not found, trying alternative buckets...');
            
            // Try homework-files bucket
            bucketName = 'homework-files';
            uploadResult = await supabase.storage
              .from(bucketName)
              .upload(filePath, file);
            uploadError = uploadResult.error;

            // If that also fails, try creating a generic uploads bucket path
            if (uploadError && uploadError.message && uploadError.message.includes('Bucket not found')) {
              console.error('No suitable bucket found. Please create a storage bucket in Supabase.');
              alert('Storage bucket not found. Please contact your administrator to set up the storage bucket (student-images or homework-files).');
              return;
            }
          }

          if (uploadError) {
            console.error('File upload error:', uploadError);
            alert(`Failed to upload file: ${uploadError.message}. Please try again or contact support.`);
            return;
          }

          // Get public URL - try public URL first, if bucket is private use signed URL
          let fileUrl;
          try {
            const { data: { publicUrl } } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);
            fileUrl = publicUrl;
          } catch (urlError) {
            console.warn('Public URL failed, trying signed URL:', urlError);
            // If public URL fails, try to get signed URL (for private buckets)
            const { data: signedUrlData, error: signedError } = await supabase.storage
              .from(bucketName)
              .createSignedUrl(filePath, 3600); // 1 hour expiry
            
            if (signedError) {
              console.error('Error getting signed URL:', signedError);
              // Fallback: construct URL manually using the supabase URL
              fileUrl = `${SUPABASE_URL}/storage/v1/object/public/${bucketName}/${filePath}`;
            } else {
              fileUrl = signedUrlData.signedUrl;
            }
          }

          attachments = [{
            name: file.name,
            url: fileUrl,
            size: file.size,
            type: file.type,
            path: filePath, // Store path for future reference
            bucket: bucketName // Store bucket name for future reference
          }];

          console.log('File uploaded successfully:', fileUrl);
        } catch (uploadErr) {
          console.error('Error uploading file:', uploadErr);
          alert('Failed to upload file. Please try again.');
          return;
        }
      }

      const homeworkPayload = {
        school_id: teacherData.school_id,
        teacher_id: teacherData.id,
        class_id: homeworkData.class_id,
        section_name: homeworkData.section_name,
        subject: homeworkData.subject,
        title: homeworkData.title,
        description: homeworkData.description,
        due_date: homeworkData.due_date,
        assigned_date: homeworkData.assigned_date
      };

      if (attachments) {
        homeworkPayload.attachments = attachments;
      }

      const { data, error } = await supabase
        .from('homework')
        .insert([homeworkPayload])
        .select();

      if (error) {
        console.error('Error adding homework:', error);
        const errorMessage = error.message || 'Unknown database error';
        const needsAttachmentsColumn = errorMessage.toLowerCase().includes('attachments');
        alert(
          needsAttachmentsColumn
            ? 'Failed to add homework with attachment. Please run supabase_update_homework_extras.sql in Supabase, then try again.'
            : `Failed to add homework: ${errorMessage}`
        );
        return;
      }

      console.log('Homework added successfully:', data);
      
      // Clear the form
      setNewHomework({
        class_key: '',
        class_id: '',
        section_name: '',
        subject: '',
        title: '',
        description: '',
        due_date: '',
        assigned_date: selectedHomeworkDate
      });
      const fileInput = e.target.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      
      // Clear file from state
      const updatedFiles = { ...homeworkFiles };
      delete updatedFiles[fileKey];
      setHomeworkFiles(updatedFiles);
      
      // Refresh homework data
      fetchHomeworkData();
      
      alert('Homework added successfully!');

    } catch (error) {
      console.error('Error adding homework:', error);
      alert(`Failed to add homework: ${error.message || 'Unknown error'}`);
    }
  };

  const handleHomeworkFileChange = (e, classId, sectionName, subject) => {
    const file = e.target.files[0];
    if (file) {
      const fileKey = `${classId}-${sectionName}-${subject}`;
      setHomeworkFiles(prev => ({
        ...prev,
        [fileKey]: file
      }));
    }
  };

  const handleDeleteHomework = async (homeworkId) => {
    if (!window.confirm('Are you sure you want to delete this homework?')) return;

    try {
      const { error } = await supabase
        .from('homework')
        .delete()
        .eq('id', homeworkId);

      if (error) {
        console.error('Error deleting homework:', error);
        alert('Failed to delete homework. Please try again.');
        return;
      }

      console.log('Homework deleted successfully');
      
      // Refresh homework data
      fetchHomeworkData();
      
      alert('Homework deleted successfully!');

    } catch (error) {
      console.error('Error deleting homework:', error);
      alert('Failed to delete homework. Please try again.');
    }
  };

  const handleEditHomework = (homework) => {
    setEditingHomework(homework.id);
    setEditHomeworkData({
      title: homework.title,
      description: homework.description,
      due_date: homework.due_date
    });
  };

  const handleSaveEdit = async (homeworkId) => {
    try {
      const { error } = await supabase
        .from('homework')
        .update({
          description: editHomeworkData.description,
          due_date: editHomeworkData.due_date,
          updated_at: new Date().toISOString()
        })
        .eq('id', homeworkId);

      if (error) {
        console.error('Error updating homework:', error);
        alert('Failed to update homework. Please try again.');
        return;
      }

      console.log('Homework updated successfully');
      
      // Refresh homework data
      fetchHomeworkData();
      
      // Reset edit state
      setEditingHomework(null);
      setEditHomeworkData({ title: '', description: '', due_date: '' });
      
      alert('Homework updated successfully!');

    } catch (error) {
      console.error('Error updating homework:', error);
      alert('Failed to update homework. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingHomework(null);
    setEditHomeworkData({ title: '', description: '', due_date: '' });
  };

  // Homework completion functions
  const handleViewHomeworkCompletion = async (homework) => {
    if (!teacherData || !homework) return;

    setSelectedHomeworkForCompletion(homework);
    setShowHomeworkCompletionModal(true);
    setHomeworkStudentsLoading(true);

    try {
      // Fetch students for this homework's class and section
      let query = supabase
        .from('students')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('class_id', homework.class_id)
        .eq('is_active', true);

      if (homework.section_name) {
        query = query.eq('section_id', homework.section_name);
      }

      const { data: students, error } = await query.order('first_name');

      if (error) {
        console.error('Error fetching students:', error);
        alert('Failed to load students. Please try again.');
        setHomeworkStudents([]);
        return;
      }

      setHomeworkStudents(students || []);

      // Fetch existing completion status
      await fetchHomeworkCompletionStatus(homework.id, students || []);

    } catch (error) {
      console.error('Error fetching homework students:', error);
      alert('Failed to load students. Please try again.');
      setHomeworkStudents([]);
    } finally {
      setHomeworkStudentsLoading(false);
    }
  };

  const fetchHomeworkCompletionStatus = async (homeworkId, studentsList) => {
    if (!homeworkId || !studentsList || studentsList.length === 0) {
      setHomeworkCompletionStatus({});
      return;
    }

    try {
      const studentIds = studentsList.map(s => s.id);
      
      const { data: completionData, error } = await supabase
        .from('homework_completion')
        .select('*')
        .eq('homework_id', homeworkId)
        .in('student_id', studentIds);

      if (error) {
        console.error('Error fetching completion status:', error);
        // Initialize all as false if fetch fails
        const status = {};
        studentsList.forEach(student => {
          status[student.id] = false;
        });
        setHomeworkCompletionStatus(status);
        return;
      }

      // Initialize completion status
      const status = {};
      studentsList.forEach(student => {
        const completion = completionData.find(c => c.student_id === student.id);
        status[student.id] = completion ? completion.is_completed : false;
      });

      setHomeworkCompletionStatus(status);

    } catch (error) {
      console.error('Error fetching completion status:', error);
      // Initialize all as false on error
      const status = {};
      studentsList.forEach(student => {
        status[student.id] = false;
      });
      setHomeworkCompletionStatus(status);
    }
  };

  const handleToggleCompletion = (studentId) => {
    setHomeworkCompletionStatus(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  const handleSaveCompletion = async () => {
    if (!selectedHomeworkForCompletion) return;

    setSavingCompletion(true);

    try {
      const homeworkId = selectedHomeworkForCompletion.id;
      const updates = [];

      // Prepare upsert operations for each student
      for (const studentId in homeworkCompletionStatus) {
        const isCompleted = homeworkCompletionStatus[studentId];
        
        const completionData = {
          homework_id: homeworkId,
          student_id: parseInt(studentId),
          is_completed: isCompleted,
          completed_at: isCompleted ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        };

        // Use upsert to insert or update
        const { error } = await supabase
          .from('homework_completion')
          .upsert(completionData, {
            onConflict: 'homework_id,student_id'
          });

        if (error) {
          console.error(`Error saving completion for student ${studentId}:`, error);
        } else {
          updates.push(studentId);
        }
      }

      setCompletionSuccessMessage(`Successfully saved completion status for ${updates.length} student(s)!`);
      setIsCompletionError(false);
      setShowCompletionSuccessPopup(true);
      setShowHomeworkCompletionModal(false);
      setSelectedHomeworkForCompletion(null);
      setHomeworkStudents([]);
      setHomeworkCompletionStatus({});
      
      // Auto-hide popup after 3 seconds
      setTimeout(() => {
        setShowCompletionSuccessPopup(false);
      }, 3000);

    } catch (error) {
      console.error('Error saving completion:', error);
      setCompletionSuccessMessage('Failed to save completion status. Please try again.');
      setIsCompletionError(true);
      setShowCompletionSuccessPopup(true);
      
      // Auto-hide error popup after 4 seconds
      setTimeout(() => {
        setShowCompletionSuccessPopup(false);
      }, 4000);
    } finally {
      setSavingCompletion(false);
    }
  };

  const handleCloseCompletionModal = () => {
    setShowHomeworkCompletionModal(false);
    setSelectedHomeworkForCompletion(null);
    setHomeworkStudents([]);
    setHomeworkCompletionStatus({});
  };

  const getTeacherClasses = () => {
    if (!teacherSchedule || Object.keys(classNames).length === 0) return [];
    
    const classes = new Set();
    teacherSchedule.forEach(schedule => {
      if (schedule.class_id && classNames[schedule.class_id]) {
        classes.add({
          class_id: schedule.class_id,
          class_name: classNames[schedule.class_id],
          section_id: schedule.section_id
        });
      }
    });
    
    return Array.from(classes);
  };

  const getClassesForDate = (selectedDate) => {
    if (!teacherSchedule || Object.keys(classNames).length === 0) {
      console.log('No teacher schedule or class names available');
      return [];
    }

    const targetDate = new Date(selectedDate);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDay = dayNames[targetDate.getDay()];

    console.log('=== CLASSES FOR DATE DEBUG ===');
    console.log('Selected date:', selectedDate);
    console.log('Target day:', targetDay);
    console.log('Teacher schedule length:', teacherSchedule.length);

    const classesForDate = [];

    teacherSchedule.forEach((schedule, index) => {
      const isTargetDay = schedule.day === targetDay || schedule.day?.toLowerCase() === targetDay;
      const hasValidClass = schedule.class_id && classNames[schedule.class_id];

      if (isTargetDay && hasValidClass) {
        const classInfo = {
          class_id: schedule.class_id,
          class_name: classNames[schedule.class_id],
          section_id: schedule.section_id,
          section_name: schedule.section_name || `Section ${schedule.section_id}`,
          subject: schedule.subject_name
        };
        
        const alreadyExists = classesForDate.some(cls => 
          cls.class_id === classInfo.class_id && 
          cls.section_id === classInfo.section_id &&
          cls.subject === classInfo.subject
        );
        
        if (!alreadyExists) {
          classesForDate.push(classInfo);
          console.log(`Added class for ${targetDay}:`, classInfo);
        }
      }
    });

    console.log(`Final classes for ${targetDay}:`, classesForDate);
    console.log('=== END CLASSES FOR DATE DEBUG ===');

    return classesForDate;
  };

  const getHomeworkClassKey = (cls) => {
    return `${cls.class_id}-${cls.section_name}-${cls.subject}`;
  };

  const getSelectedHomeworkClass = () => {
    return getClassesForDate(selectedHomeworkDate).find(cls => getHomeworkClassKey(cls) === newHomework.class_key);
  };

  const handleHomeworkDateChange = (date) => {
    const today = getTodayISODateString();
    const nextDate = date < today ? today : date;

    setSelectedHomeworkDate(nextDate);
    setNewHomework(prev => ({
      ...prev,
      class_key: '',
      class_id: '',
      section_name: '',
      subject: '',
      assigned_date: nextDate,
      due_date: prev.due_date && prev.due_date >= nextDate ? prev.due_date : addDaysToISODateString(nextDate, 7)
    }));
  };

  const handleHomeworkClassChange = (classKey) => {
    const selectedClassForHomework = getClassesForDate(selectedHomeworkDate).find(cls => getHomeworkClassKey(cls) === classKey);

    setNewHomework(prev => ({
      ...prev,
      class_key: classKey,
      class_id: selectedClassForHomework?.class_id || '',
      section_name: selectedClassForHomework?.section_name || '',
      subject: selectedClassForHomework?.subject || '',
      title: selectedClassForHomework?.subject ? `${selectedClassForHomework.subject} Homework` : '',
      assigned_date: selectedHomeworkDate,
      due_date: prev.due_date || addDaysToISODateString(selectedHomeworkDate, 7)
    }));
  };

  const getSubjectsForClass = (classId, sectionId) => {
    if (!teacherSchedule) return [];
    
    const subjects = new Set();
    teacherSchedule.forEach(schedule => {
      if (schedule.class_id === classId && schedule.section_id === sectionId && schedule.subject) {
        subjects.add(schedule.subject);
      }
    });
    
    return Array.from(subjects);
  };

  useEffect(() => {
    fetchTeacherData();
  }, []);

  useEffect(() => {
    if (activeTab === 'homework' && teacherData) {
      fetchHomeworkData();
    }
  }, [activeTab, teacherData]);

  useEffect(() => {
    if (teacherData?.school_id) {
      fetchClassNames();
      fetchTeacherSchedule();
      fetchSchoolTimings();
    }
  }, [teacherData]);

  useEffect(() => {
    // Fetch attendance classes when teacher data, class names, or schedule is available
    if (teacherData && Object.keys(classNames).length > 0) {
      fetchAttendanceClasses();
    }
  }, [teacherData, teacherSchedule, classNames]);

  useEffect(() => {
    fetchStudents();
  }, [selectedClass, selectedSection]);

  useEffect(() => {
    if (selectedClass) {
      fetchSectionsForClass();
      setSelectedSection(''); // Reset section when class changes
    } else {
      setAvailableSections([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection && students.length > 0) {
      // Set default attendance as "present" for all students first
      const defaultAttendance = {};
      students.forEach(student => {
        defaultAttendance[student.id] = 'present';
      });
      setAttendanceData(defaultAttendance);
      
      // Then fetch existing attendance to override defaults where records exist
      fetchExistingAttendance();
    } else {
      // Clear attendance data when no class/section selected
      setAttendanceData({});
      setExistingAttendance({});
    }
  }, [selectedClass, selectedSection, students, selectedAttendanceDate]);

  useEffect(() => {
    // Check for new day when component mounts or when switching to attendance tab
    if (activeTab === 'attendance') {
      checkForNewDay();
    }
  }, [activeTab]);

  useEffect(() => {
    // Fetch attendance records when month/year changes
    if (selectedClass && selectedSection && teacherData?.school_id) {
      fetchAttendanceRecords();
    }
  }, [selectedMonth, selectedYear, selectedClass, selectedSection]);

  useEffect(() => {
    if (activeTab === 'homework' && teacherData) {
      fetchHomeworkData();
    }
  }, [activeTab, teacherData]);

  useEffect(() => {
    if (activeTab === 'exams' && teacherData && teacherSchedule.length > 0) {
      fetchTeacherExams();
    }
  }, [activeTab, teacherData, teacherSchedule]);

  useEffect(() => {
    if (activeTab === 'chat' && teacherData) {
      fetchChatStudents();
      fetchTeacherChatGroups();
    }
  }, [activeTab, teacherData]);

  useEffect(() => {
    if (activeTab === 'chat' && selectedChatStudent && teacherData && !chatGroup) {
      loadOrCreateChatGroup();
    } else if (activeTab === 'chat' && !selectedChatStudent && !selectedChatGroup) {
      setChatMessages([]);
      setChatGroup(null);
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }
    } else if (activeTab === 'chat' && !selectedChatStudent && selectedChatGroup) {
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
    } else if (activeTab === 'chat' && selectedChatStudent && !selectedChatGroup) {
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }
    }

    return () => {
      if (activeTab !== 'chat') {
        if (window.currentChatChannel) {
          supabase.removeChannel(window.currentChatChannel);
          window.currentChatChannel = null;
        }
        if (window.currentGroupChatChannel) {
          supabase.removeChannel(window.currentGroupChatChannel);
          window.currentGroupChatChannel = null;
        }
      }
    };
  }, [selectedChatStudent, selectedChatGroup, activeTab, teacherData, chatGroup]);

  // Separate effect for polling messages (only when chat group exists)
  useEffect(() => {
    let messagePollInterval = null;

    if (activeTab === 'chat' && chatGroup && chatGroup.id && !chatLoading) {
      // Fallback: Poll for new messages every 3 seconds if real-time doesn't work
      messagePollInterval = setInterval(async () => {
        const { data: latestMessages } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('group_id', chatGroup.id)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (latestMessages && latestMessages.length > 0) {
          const latestMessage = latestMessages[0];
          setChatMessages(prev => {
            const exists = prev.some(m => m.id === latestMessage.id);
            if (!exists) {
              return [...prev, latestMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            }
            return prev;
          });
        }
      }, 3000);
    }

    return () => {
      if (messagePollInterval) {
        clearInterval(messagePollInterval);
      }
    };
  }, [chatGroup, activeTab, chatLoading]);

  // Auto-scroll to bottom when messages change and mark as read (individual chat)
  useEffect(() => {
    if (activeTab === 'chat' && chatMessages.length > 0 && selectedChatStudent) {
      // Only scroll if we have a selected student (not group chat)
      // Use a longer timeout to avoid conflicts with real-time subscription scroll
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      // Mark messages as read when user views the chat
      if (selectedChatStudent && teacherData && chatMessages.length > 0) {
        const lastMessage = chatMessages[chatMessages.length - 1];
        const lastReadKey = `lastRead_${teacherData.id}_${selectedChatStudent.id}`;
        localStorage.setItem(lastReadKey, lastMessage.created_at);
        
        // Update unread count to 0
        setUnreadCounts(prev => ({
          ...prev,
          [selectedChatStudent.id]: 0
        }));
      }
    }
  }, [chatMessages.length, activeTab, selectedChatStudent, teacherData]);

  // Auto-scroll to bottom when group chat messages change
  useEffect(() => {
    if (activeTab === 'chat' && groupChatMessages.length > 0 && selectedChatGroup) {
      setTimeout(() => {
        const messagesArea = document.querySelector('.chat-messages-area');
        if (messagesArea) {
          messagesArea.scrollTop = messagesArea.scrollHeight;
        }
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [groupChatMessages.length, activeTab, selectedChatGroup]);

  const scrollToBottom = () => {
    setTimeout(() => {
      // Only scroll if we have a selected student (individual chat, not group chat)
      if (selectedChatStudent && chatMessagesEndRef.current) {
        // Use the ref method which is more reliable
        chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
      // Also try the messages area method as fallback
      const messagesArea = document.querySelector('.chat-messages-area');
      if (messagesArea && selectedChatStudent) {
        messagesArea.scrollTop = messagesArea.scrollHeight;
      }
    }, 200);
  };

  const fetchTeacherNotices = async () => {
    try {
      setNoticesLoading(true);
      console.log('Fetching notices for teachers');
      
      // Check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setNotices([]);
        return;
      }

      if (!session) {
        console.error('No active session');
        setNotices([]);
        return;
      }

      // Fetch all teacher notices
      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .eq('target_audience', 'teachers')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching teacher notices:', error);
        if (error.code === 'PGRST301' || error.message.includes('JWT')) {
          console.error('Authentication expired');
        } else {
          console.error('Database error:', error);
        }
        setNotices([]);
        return;
      }

      console.log('Fetched teacher notices:', data);
      setNotices(data || []);
    } catch (err) {
      console.error('Error fetching teacher notices:', err);
      setNotices([]);
    } finally {
      setNoticesLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('teacherData');
    localStorage.removeItem('teacherAuthenticated');
    localStorage.removeItem('supabaseAuthToken');
    navigate('/');
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'normal': return '🟢';
      default: return '🟢';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'High Priority';
      case 'medium': return 'Medium Priority';
      case 'normal': return 'Normal Priority';
      default: return 'Normal Priority';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'high-priority';
      case 'medium': return 'medium-priority';
      case 'normal': return 'normal-priority';
      default: return 'normal-priority';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const fetchChatStudents = async () => {
    if (!teacherData?.id || !teacherData?.school_id) return;

    try {
      setChatStudentsLoading(true);
      
      // Fetch all chat groups where this teacher is a member
      const { data: groupMembers, error: membersError } = await supabase
        .from('chat_group_members')
        .select('group_id')
        .eq('user_type', 'teacher')
        .eq('user_id', teacherData.id.toString());

      if (membersError) {
        console.error('Error fetching chat groups:', membersError);
        setChatStudents([]);
        return;
      }

      if (!groupMembers || groupMembers.length === 0) {
        setChatStudents([]);
        return;
      }

      const groupIds = groupMembers.map(m => m.group_id);

      // Get all groups and their members
      const { data: groups, error: groupsError } = await supabase
        .from('chat_groups')
        .select('id, name, school_id')
        .in('id', groupIds)
        .eq('school_id', teacherData.school_id);

      if (groupsError) {
        console.error('Error fetching groups:', groupsError);
        setChatStudents([]);
        return;
      }

      // For each group, get student members
      const studentsMap = {};
      for (const group of groups || []) {
        const { data: members, error: membersErr } = await supabase
          .from('chat_group_members')
          .select('user_id, user_type, display_name')
          .eq('group_id', group.id)
          .eq('user_type', 'student');

        if (!membersErr && members) {
          for (const member of members) {
            if (!studentsMap[member.user_id]) {
              // Fetch student details with class information
              const { data: student, error: studentErr } = await supabase
                .from('students')
                .select('id, first_name, last_name, middle_name, class_id, section_id, student_id_no, classes(class_name)')
                .eq('id', member.user_id)
                .single();

              if (!studentErr && student) {
                // Extract class_name from the classes relationship
                // Supabase returns it as an object for foreign key relationships
                const className = student.classes?.class_name || 
                                 (Array.isArray(student.classes) && student.classes[0]?.class_name) || 
                                 null;
                
                studentsMap[member.user_id] = {
                  ...student,
                  class_name: className,
                  lastMessageTime: null,
                  unreadCount: 0
                };
              }
            }
          }
        }
      }

      // Get last message time for each student
      for (const studentId in studentsMap) {
        const { data: lastMessage } = await supabase
          .from('chat_messages')
          .select('created_at, sender_type')
          .eq('group_id', groupIds[0]) // This is simplified - should check all groups
          .or(`sender_id.eq.${studentId},sender_id.eq.${teacherData.id.toString()}`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (lastMessage) {
          studentsMap[studentId].lastMessageTime = lastMessage.created_at;
        }
      }

      const studentsList = Object.values(studentsMap);
      
      // Add admin (school) to the list
      const adminContact = {
        id: 'admin',
        first_name: 'School',
        last_name: 'Admin',
        class_name: 'Administration',
        section_id: null,
        isAdmin: true
      };
      
      setChatStudents([adminContact, ...studentsList]);
      
      // Calculate unread messages for each student (including admin)
      await calculateUnreadCounts([adminContact, ...studentsList]);
    } catch (err) {
      console.error('Error fetching chat students:', err);
      setChatStudents([]);
    } finally {
      setChatStudentsLoading(false);
    }
  };

  const calculateUnreadCounts = async (studentsList) => {
    if (!teacherData || !studentsList || studentsList.length === 0) return;

    const counts = {};
    
    for (const student of studentsList) {
      try {
        // Handle admin separately
        if (student.isAdmin) {
          const { data: groups } = await supabase
            .from('chat_groups')
            .select('id')
            .eq('school_id', teacherData.school_id)
            .eq('group_type', 'custom')
            .ilike('name', `%Admin Chat%`);

          if (groups && groups.length > 0) {
            for (const group of groups) {
              const { data: members } = await supabase
                .from('chat_group_members')
                .select('*')
                .eq('group_id', group.id);

              const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacherData.id.toString());
              const hasAdmin = members?.some(m => m.user_type === 'school');

              if (hasTeacher && hasAdmin) {
                const lastReadKey = `lastRead_${teacherData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);

                let query = supabase
                  .from('chat_messages')
                  .select('id', { count: 'exact', head: false })
                  .eq('group_id', group.id)
                  .eq('sender_type', 'school');

                if (lastReadTimestamp) {
                  query = query.gt('created_at', lastReadTimestamp);
                }

                const { count } = await query;
                counts['admin'] = count || 0;
                break;
              }
            }
          } else {
            counts['admin'] = 0;
          }
          continue;
        }

        // Find chat group for this student
        const { data: groups } = await supabase
          .from('chat_groups')
          .select('id')
          .eq('school_id', teacherData.school_id)
          .eq('group_type', 'custom')
          .ilike('name', `%${student.first_name}%${teacherData.first_name}%`);

        if (groups && groups.length > 0) {
          // Check if both are members
          for (const group of groups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', group.id);

            const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === student.id.toString());
            const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacherData.id.toString());

            if (hasStudent && hasTeacher) {
              // Get last read timestamp from localStorage
              const lastReadKey = `lastRead_${teacherData.id}_${student.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);

              // Count unread messages (messages from student after last read)
              let query = supabase
                .from('chat_messages')
                .select('id', { count: 'exact', head: false })
                .eq('group_id', group.id)
                .eq('sender_type', 'student')
                .eq('sender_id', student.id.toString());

              if (lastReadTimestamp) {
                query = query.gt('created_at', lastReadTimestamp);
              }

              const { count } = await query;
              counts[student.id] = count || 0;
              break;
            }
          }
        } else {
          counts[student.id] = 0;
        }
      } catch (err) {
        console.error(`Error calculating unread for student ${student.id}:`, err);
        counts[student.id] = 0;
      }
    }

    setUnreadCounts(counts);
  };

  // Fetch teacher chat groups - Simple and clean
  const fetchTeacherChatGroups = async () => {
    if (!teacherData?.id || !teacherData?.school_id) return;

    try {
      setLoadingGroups(true);
      
      const { data: memberships, error: membersError } = await supabase
        .from('chat_group_members')
        .select('group_id')
        .eq('user_type', 'teacher')
        .eq('user_id', teacherData.id.toString());

      if (membersError || !memberships || memberships.length === 0) {
        setChatGroups([]);
        return;
      }

      const groupIds = memberships.map(m => m.group_id);
      const { data: groups, error: groupsError } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .in('id', groupIds)
        .order('created_at', { ascending: false });

      if (groupsError) {
        setChatGroups([]);
        return;
      }

      // Get last message and member count for each group
      const enrichedGroups = await Promise.all(
        (groups || []).map(async (group) => {
          const { data: lastMessage } = await supabase
            .from('chat_messages')
            .select('created_at, content, sender_type, sender_id')
            .eq('group_id', group.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          const { data: members } = await supabase
            .from('chat_group_members')
            .select('id')
            .eq('group_id', group.id);

          return {
            ...group,
            lastMessage: lastMessage?.content || null,
            lastMessageTime: lastMessage?.created_at || null,
            lastMessageSender: lastMessage ? {
              type: lastMessage.sender_type,
              id: lastMessage.sender_id
            } : null,
            memberCount: members?.length || 0
          };
        })
      );

      setChatGroups(enrichedGroups);
    } catch (err) {
      console.error('Error fetching chat groups:', err);
      setChatGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Load group chat - Simple and clean like student chat
  const loadGroupChat = async (group) => {
    if (!group || !teacherData) return;

    try {
      setGroupChatLoading(true);
      setSelectedChatGroup(group);
      setCurrentGroupChatGroup(group);
      setSelectedChatStudent(null);
      setChatGroup(null);

      // Load messages
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading messages:', error);
        return;
      }

      // Enrich messages with sender names
      const enrichedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          let senderName = 'Unknown';
          
          if (message.sender_type === 'teacher' && message.sender_id === teacherData.id.toString()) {
            senderName = 'You';
          } else if (message.sender_type === 'student') {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (student) senderName = `${student.first_name} ${student.last_name}`;
          } else if (message.sender_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (teacher) senderName = `${teacher.first_name} ${teacher.last_name}`;
          } else if (message.sender_type === 'parent') {
            const { data: parent } = await supabase
              .from('parents')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (parent) senderName = `${parent.first_name} ${parent.last_name}`;
          } else if (message.sender_type === 'school') {
            senderName = 'School Admin';
          }

          return { ...message, senderName };
        })
      );

      setGroupChatMessages(enrichedMessages);

      // Clean up existing channel
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }

      // Set up real-time subscription - Simple like student chat
      const channel = supabase
        .channel(`group-chat:${group.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, (payload) => {
          const newMessage = payload.new;
          
          if (newMessage.group_id !== group.id) return;

          setGroupChatMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) return prev;
            
            // Quick sender name check
            let senderName = 'Unknown';
            if (newMessage.sender_type === 'teacher' && newMessage.sender_id === teacherData.id.toString()) {
              senderName = 'You';
            } else if (newMessage.sender_type === 'school') {
              senderName = 'School Admin';
            }
            
            const messageWithSender = { ...newMessage, senderName };
            const updated = [...prev, messageWithSender].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            
            // Enrich in background if needed
            if (senderName === 'Unknown') {
              (async () => {
                let name = 'Unknown';
                if (newMessage.sender_type === 'student') {
                  const { data } = await supabase.from('students').select('first_name, last_name').eq('id', newMessage.sender_id).single();
                  if (data) name = `${data.first_name} ${data.last_name}`;
                } else if (newMessage.sender_type === 'teacher') {
                  const { data } = await supabase.from('teachers').select('first_name, last_name').eq('id', newMessage.sender_id).single();
                  if (data) name = `${data.first_name} ${data.last_name}`;
                } else if (newMessage.sender_type === 'parent') {
                  const { data } = await supabase.from('parents').select('first_name, last_name').eq('id', newMessage.sender_id).single();
                  if (data) name = `${data.first_name} ${data.last_name}`;
                }
                setGroupChatMessages(prevMsgs => prevMsgs.map(msg => msg.id === newMessage.id ? { ...msg, senderName: name } : msg));
              })();
            }
            
            return updated;
          });

          // Scroll to bottom
          setTimeout(() => {
            if (chatMessagesEndRef.current) {
              chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        })
        .subscribe();

      window.currentGroupChatChannel = channel;

      // Scroll to bottom
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error loading group chat:', err);
    } finally {
      setGroupChatLoading(false);
    }
  };

  // Send group message - Simple like student chat
  const sendGroupMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedChatGroup || !teacherData) return;

    try {
      // Upload files if any
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data: message, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: teacherData.school_id,
          group_id: selectedChatGroup.id,
          sender_type: 'teacher',
          sender_id: teacherData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }

      // Add message immediately for instant feedback
      setGroupChatMessages(prev => {
        const exists = prev.some(m => m.id === message.id);
        if (exists) return prev;
        return [...prev, { ...message, senderName: 'You' }].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      
      setNewMessage('');

      // Scroll to bottom
      setTimeout(() => {
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      console.error('Error sending group message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const loadOrCreateChatGroup = async () => {
    if (!selectedChatStudent || !teacherData) return;

    try {
      setChatLoading(true);
      
      // Handle admin chat
      if (selectedChatStudent.isAdmin) {
        const groupName = `Admin Chat: ${teacherData.first_name} ${teacherData.last_name}`;
        
        // Check if admin chat group already exists
        let { data: existingGroups, error: groupError } = await supabase
          .from('chat_groups')
          .select('*')
          .eq('school_id', teacherData.school_id)
          .eq('group_type', 'custom')
          .ilike('name', `%Admin Chat%${teacherData.first_name}%`);

        let group = null;

        if (existingGroups && existingGroups.length > 0) {
          for (const g of existingGroups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', g.id);

            const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacherData.id.toString());
            const hasAdmin = members?.some(m => m.user_type === 'school' && m.user_id === teacherData.school_id.toString());

            if (hasTeacher && hasAdmin) {
              group = g;
              break;
            }
          }
        }

        // Create new admin chat group if doesn't exist
        if (!group) {
          const { data: newGroup, error: createError } = await supabase
            .from('chat_groups')
            .insert({
              school_id: teacherData.school_id,
              name: groupName,
              group_type: 'custom',
              created_by: 'teacher'
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating admin chat group:', createError);
            alert('Failed to create chat. Please try again.');
            return;
          }

          group = newGroup;

          // Add teacher as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'teacher',
              user_id: teacherData.id.toString(),
              display_name: `${teacherData.first_name} ${teacherData.last_name}`
            });

          // Add admin (school) as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'school',
              user_id: teacherData.school_id.toString(),
              display_name: 'School Admin'
            });
        }

        setChatGroup(group);

        // Load messages
        const { data: messages, error: messagesError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('group_id', group.id)
          .order('created_at', { ascending: true });

        if (messagesError) {
          console.error('Error loading messages:', messagesError);
        } else {
          setChatMessages(messages || []);
          
          // Mark messages as read
          if (messages && messages.length > 0 && selectedChatStudent) {
            const lastMessage = messages[messages.length - 1];
            const lastReadKey = `lastRead_${teacherData.id}_admin`;
            localStorage.setItem(lastReadKey, lastMessage.created_at);
            
            setUnreadCounts(prev => ({
              ...prev,
              'admin': 0
            }));
          }
        }

        // Clean up existing subscription
        if (window.currentChatChannel) {
          supabase.removeChannel(window.currentChatChannel);
          window.currentChatChannel = null;
        }
        if (window.currentGroupChatChannel) {
          supabase.removeChannel(window.currentGroupChatChannel);
          window.currentGroupChatChannel = null;
        }

        // Set up real-time subscription
        const channel = supabase
          .channel(`chat:${group.id}`)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `group_id=eq.${group.id}`
          }, (payload) => {
            const newMessage = payload.new;
            
            if (newMessage.group_id !== group.id) return;

            setChatMessages(prev => {
              const exists = prev.some(m => m.id === newMessage.id);
              if (exists) return prev;
              
              if (newMessage.sender_type === 'school' && selectedChatStudent) {
                const lastReadKey = `lastRead_${teacherData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                
                if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                  setUnreadCounts(prevCounts => ({
                    ...prevCounts,
                    'admin': (prevCounts['admin'] || 0) + 1
                  }));
                }
              }
              
              return [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            });

            setTimeout(() => {
              if (chatMessagesEndRef.current) {
                chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          })
          .subscribe();

        window.currentChatChannel = channel;

        setTimeout(() => {
          if (chatMessagesEndRef.current) {
            chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        
        setChatLoading(false);
        return;
      }

      // Regular student chat (existing code)
      const groupName = `Chat: ${selectedChatStudent.first_name} ${selectedChatStudent.last_name} - ${teacherData.first_name} ${teacherData.last_name}`;

      // Check if chat group already exists
      let { data: existingGroups, error: groupError } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', teacherData.school_id)
        .eq('group_type', 'custom')
        .ilike('name', `%${selectedChatStudent.first_name}%${teacherData.first_name}%`);

      let group = null;

      if (existingGroups && existingGroups.length > 0) {
        // Check if both student and teacher are members
        for (const g of existingGroups) {
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('*')
            .eq('group_id', g.id);

          const hasStudent = members?.some(m => m.user_type === 'student' && m.user_id === selectedChatStudent.id.toString());
          const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacherData.id.toString());

          if (hasStudent && hasTeacher) {
            group = g;
            break;
          }
        }
      }

      // Create new group if doesn't exist
      if (!group) {
        const { data: newGroup, error: createError } = await supabase
          .from('chat_groups')
          .insert({
            school_id: teacherData.school_id,
            name: groupName,
            group_type: 'custom',
            created_by: 'teacher'
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating chat group:', createError);
          alert('Failed to create chat. Please try again.');
          return;
        }

        group = newGroup;

        // Add student as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'student',
            user_id: selectedChatStudent.id.toString(),
            display_name: `${selectedChatStudent.first_name} ${selectedChatStudent.last_name}`
          });

        // Add teacher as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'teacher',
            user_id: teacherData.id.toString(),
            display_name: `${teacherData.first_name} ${teacherData.last_name}`
          });
      }

      setChatGroup(group);

      // Load messages
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else {
        setChatMessages(messages || []);
        
        // Mark messages as read - update last read timestamp
        if (messages && messages.length > 0 && selectedChatStudent) {
          const lastMessage = messages[messages.length - 1];
          const lastReadKey = `lastRead_${teacherData.id}_${selectedChatStudent.id}`;
          localStorage.setItem(lastReadKey, lastMessage.created_at);
          
          // Update unread count to 0 for this student
          setUnreadCounts(prev => ({
            ...prev,
            [selectedChatStudent.id]: 0
          }));
        }
      }

      // Clean up any existing subscription first
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
      // Clean up group chat channel when switching to student chat
      if (window.currentGroupChatChannel) {
        supabase.removeChannel(window.currentGroupChatChannel);
        window.currentGroupChatChannel = null;
      }

      // Set up real-time subscription for new messages
      const channel = supabase
        .channel(`chat:${group.id}`, {
          config: {
            broadcast: { self: false }
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, (payload) => {
          console.log('New message received via real-time:', payload.new);
          const newMessage = payload.new;
          
          // Verify the message belongs to the current chat group
          if (newMessage.group_id !== group.id) {
            console.log('Message group_id does not match current group, ignoring');
            return;
          }
          
          setChatMessages(prev => {
            // Check if message already exists to avoid duplicates
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) {
              console.log('Message already exists, skipping duplicate');
              return prev;
            }
            console.log('Adding new message to chat. Previous count:', prev.length);
            
            // If message is from student or admin and chat is not currently open, increment unread count
            if ((newMessage.sender_type === 'student' || newMessage.sender_type === 'school') && selectedChatStudent) {
              const lastReadKey = selectedChatStudent.isAdmin 
                ? `lastRead_${teacherData.id}_admin`
                : `lastRead_${teacherData.id}_${selectedChatStudent.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);
              
              // Only increment if message is newer than last read
              if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                const countKey = selectedChatStudent.isAdmin ? 'admin' : selectedChatStudent.id;
                setUnreadCounts(prevCounts => ({
                  ...prevCounts,
                  [countKey]: (prevCounts[countKey] || 0) + 1
                }));
              }
            }
            
            // Sort messages by created_at to ensure proper order
            const updatedMessages = [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            console.log('Updated messages count:', updatedMessages.length);
            return updatedMessages;
          });

          // Scroll to bottom when new message arrives
          setTimeout(() => {
            // Use multiple methods to ensure scroll works
            const messagesArea = document.querySelector('.chat-messages-area');
            if (messagesArea) {
              messagesArea.scrollTop = messagesArea.scrollHeight;
            }
            if (chatMessagesEndRef.current) {
              chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
          }, 200);
        })
        .subscribe((status) => {
          console.log('Subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('Successfully subscribed to chat messages for group:', group.id);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('Channel subscription error, attempting to resubscribe...');
            // Try to resubscribe after a short delay
            setTimeout(() => {
              if (selectedChatStudent && chatGroup && chatGroup.id === group.id) {
                console.log('Resubscribing to chat channel...');
                loadOrCreateChatGroup();
              }
            }, 2000);
          } else if (status === 'TIMED_OUT') {
            console.warn('Subscription timed out, attempting to resubscribe...');
            setTimeout(() => {
              if (selectedChatStudent && chatGroup && chatGroup.id === group.id) {
                console.log('Resubscribing to chat channel after timeout...');
                loadOrCreateChatGroup();
              }
            }, 2000);
          } else if (status === 'CLOSED') {
            console.warn('Subscription closed, attempting to resubscribe...');
            setTimeout(() => {
              if (selectedChatStudent && chatGroup && chatGroup.id === group.id) {
                console.log('Resubscribing to chat channel after close...');
                loadOrCreateChatGroup();
              }
            }, 2000);
          }
        });

      // Store channel reference for cleanup
      window.currentChatChannel = channel;

      // Scroll to bottom after loading messages
      setTimeout(() => {
        const messagesArea = document.querySelector('.chat-messages-area');
        if (messagesArea) {
          messagesArea.scrollTop = messagesArea.scrollHeight;
        }
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 300);
    } catch (err) {
      console.error('Error loading chat:', err);
      alert('Failed to load chat. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  // Upload files to Supabase storage
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadedUrls = [];
    setUploadingFiles(true);
    
    try {
      // Ensure chat-uploads bucket exists (create if needed)
      const bucketName = 'chat-uploads';
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${teacherData.school_id}/${fileName}`;
        
        // Upload file
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          // Try to create bucket if it doesn't exist
          if (uploadError.message.includes('Bucket not found')) {
            alert('Storage bucket not found. Please create "chat-uploads" bucket in Supabase Storage.');
            continue;
          }
          continue;
        }
        
        // Get public URL
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        
        uploadedUrls.push({
          url: urlData.publicUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedUrls;
  };

  const sendMessage = async (e) => {
    // Prevent form submission and page refresh
    if (e) {
      e.preventDefault();
    }
    
    if ((!newMessage.trim() && selectedFiles.length === 0) || !chatGroup || !teacherData) return;

    try {
      // Upload files if any
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: teacherData.school_id,
          group_id: chatGroup.id,
          sender_type: 'teacher',
          sender_id: teacherData.id.toString(),
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }

      // Add message immediately for instant feedback
      // Real-time subscription will also add it, but we check for duplicates
      setChatMessages(prev => {
        const exists = prev.some(m => m.id === data.id);
        if (exists) return prev;
        // Sort messages by created_at to ensure proper order
        return [...prev, data].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      setNewMessage('');

      // Scroll to bottom after sending message
      setTimeout(() => {
        const messagesArea = document.querySelector('.chat-messages-area');
        if (messagesArea) {
          messagesArea.scrollTop = messagesArea.scrollHeight;
        }
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 200);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    // Limit to 5 files and 10MB per file
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    }).slice(0, 5);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Mark message as read
  const markMessageAsRead = async (messageId, message) => {
    if (!message || !teacherData) return;
    
    // Only mark messages sent by others as read
    if (message.sender_type === 'teacher' && message.sender_id === teacherData.id.toString()) {
      return; // Don't mark own messages
    }

    try {
      const readBy = message.read_by || [];
      const readerId = `${teacherData.id}`;
      const readerType = 'teacher';
      
      // Check if already marked as read by this user
      const alreadyRead = readBy.some(
        reader => reader.user_id === readerId && reader.user_type === readerType
      );
      
      if (alreadyRead) return;

      // Add current user to read_by array
      const updatedReadBy = [
        ...readBy,
        {
          user_id: readerId,
          user_type: readerType,
          read_at: new Date().toISOString()
        }
      ];

      await supabase
        .from('chat_messages')
        .update({ read_by: updatedReadBy })
        .eq('id', messageId);

      // Update local state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, read_by: updatedReadBy }
            : msg
        )
      );
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  // Get message status indicator
  const getMessageStatus = (message) => {
    if (!message || message.sender_type !== 'teacher' || message.sender_id !== teacherData.id.toString()) {
      return null; // Only show status for own messages
    }

    const readBy = message.read_by || [];
    
    // Check if recipient has read the message
    if (selectedChatStudent) {
      const recipientId = selectedChatStudent.isAdmin 
        ? teacherData.school_id.toString() 
        : selectedChatStudent.id.toString();
      const recipientType = selectedChatStudent.isAdmin ? 'school' : 'student';
      
      const isRead = readBy.some(
        reader => reader.user_id === recipientId && reader.user_type === recipientType
      );
      
      if (isRead) {
        return { status: 'read', icon: '✓✓', color: '#4F46E5' }; // Blue double check
      }
    }
    
    // Message is delivered (exists in DB) but not read
    return { status: 'delivered', icon: '✓✓', color: '#9CA3AF' }; // Gray double check
  };

  // AI Assistant Functions
  const sendAiMessage = async () => {
    if (!aiChatInput.trim() || aiChatLoading) return;

    const userMessage = aiChatInput.trim();
    const timestamp = new Date().toISOString();

    // Add user message to chat
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      timestamp
    };
    setAiChatMessages(prev => [...prev, userMsg]);
    setAiChatInput('');
    setAiChatLoading(true);

    try {
      // Try Google Gemini API first
      let aiResponse = null;
      try {
        aiResponse = await tryGoogleGemini(userMessage);
      } catch (geminiError) {
        console.log('Gemini API not available, using smart assistant:', geminiError);
        // Fall through to use smart assistant
      }
      
      // If Gemini fails, use smart rule-based assistant
      if (!aiResponse) {
        aiResponse = getSmartAssistantResponse(userMessage);
      }

      // Add AI response to chat
      const aiMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      
      // Fallback to smart assistant
      const fallbackResponse = getSmartAssistantResponse(userMessage);
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: fallbackResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, errorMsg]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // List available Gemini models
  const listAvailableModels = async (apiKey) => {
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      );
      if (response.ok) {
        const data = await response.json();
        return data.models?.map(m => m.name) || [];
      }
    } catch (err) {
      console.error('Error listing models:', err);
    }
    return [];
  };

  // Use Google Gemini API
  const tryGoogleGemini = async (question) => {
    // Get API key from localStorage (default key is set automatically)
    const geminiApiKey = localStorage.getItem('gemini_api_key') || 'AIzaSyBIRBgSh5JpYKU2-9sW4-XAeZNyRK5HzCg';
    
    // First, try to get available models
    const availableModels = await listAvailableModels(geminiApiKey);
    console.log('Available Gemini models:', availableModels);
    
    // Try different API endpoints and models
    // Prioritize newer models and available models from API
    // Filter out embedding models (they don't support generateContent)
    const textGenerationModels = availableModels.length > 0 
      ? availableModels
          .filter(modelName => 
            !modelName.includes('embedding') && 
            !modelName.includes('imagen') && 
            !modelName.includes('veo') &&
            !modelName.includes('aqa')
          )
          .map(modelName => ({
            version: 'v1beta',
            model: modelName.replace('models/', '')
          }))
      : [];
    
    const apiConfigs = [
      // First, try available models from API (most reliable)
      ...textGenerationModels,
      // Then try latest stable models (v2.5)
      { version: 'v1beta', model: 'gemini-2.5-flash' },
      { version: 'v1beta', model: 'gemini-2.5-pro' },
      { version: 'v1beta', model: 'gemini-flash-latest' },
      { version: 'v1beta', model: 'gemini-pro-latest' },
      // Try v2.0 models as fallback
      { version: 'v1beta', model: 'gemini-2.0-flash' },
      { version: 'v1beta', model: 'gemini-2.0-pro-exp' },
      // Legacy models (last resort)
      { version: 'v1beta', model: 'gemini-1.5-flash' },
      { version: 'v1beta', model: 'gemini-1.5-pro' },
    ];
    
    for (const config of apiConfigs) {
      try {
        const url = `https://generativelanguage.googleapis.com/${config.version}/models/${config.model}:generateContent?key=${geminiApiKey}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `You are a helpful and friendly AI assistant for teachers. Help them with lesson planning, student management, grading strategies, classroom management tips, and educational best practices. Be encouraging and provide clear, practical advice.

Teacher Question: ${question}

Provide a helpful, educational response:`
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024,
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(`Gemini API HTTP error (${config.version}/${config.model}):`, response.status, errorData);
          
          // Log detailed error for debugging
          if (errorData.error) {
            console.error('Detailed error:', {
              code: errorData.error.code,
              message: errorData.error.message,
              status: errorData.error.status
            });
          }
          
          // If it's a model not found error (404), try next config
          if (response.status === 404) {
            continue; // Try next config
          }
          
          // If it's authentication error (401/403), don't try other configs
          if (response.status === 401 || response.status === 403) {
            throw new Error(errorData.error?.message || `Authentication error: ${response.status}. Please check your API key.`);
          }
          
          // If it's the last config, return null to use fallback
          if (config === apiConfigs[apiConfigs.length - 1]) {
            return null; // Use fallback instead of throwing
          }
          continue;
        }

        const data = await response.json();
        console.log('Gemini API response:', data);
        
        if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
          return data.candidates[0].content.parts[0].text.trim();
        } else if (data.error) {
          console.error('Gemini API error in response:', data.error);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error(data.error.message || 'Failed to get response');
        } else {
          console.error('Unexpected response format:', data);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error('Unexpected response format from API');
        }
      } catch (err) {
        console.error(`Gemini API error (${config.version}/${config.model}):`, err);
        // If it's the last config, throw the error
        if (config === apiConfigs[apiConfigs.length - 1]) {
          throw err;
        }
        // Otherwise, continue to next config
        continue;
      }
    }
    
    // If we get here, all configs failed - return null to use fallback
    return null;
  };

  // Smart Rule-Based Assistant - Works without API
  const getSmartAssistantResponse = (question) => {
    const lowerQuestion = question.toLowerCase().trim();
    
    // Math calculations
    if (lowerQuestion.match(/\d+\s*[+\-*/×÷]\s*\d+/)) {
      try {
        const mathMatch = question.match(/(\d+)\s*([+\-*/×÷])\s*(\d+)/);
        if (mathMatch) {
          const num1 = parseFloat(mathMatch[1]);
          const op = mathMatch[2] === '×' ? '*' : mathMatch[2] === '÷' ? '/' : mathMatch[2];
          const num2 = parseFloat(mathMatch[3]);
          let result;
          switch(op) {
            case '+': result = num1 + num2; break;
            case '-': result = num1 - num2; break;
            case '*': result = num1 * num2; break;
            case '/': result = num2 !== 0 ? (num1 / num2).toFixed(2) : 'undefined (division by zero)'; break;
            default: break;
          }
          if (result !== undefined) {
            return `The answer is: **${result}**\n\n${num1} ${op} ${num2} = ${result}`;
          }
        }
      } catch (e) {}
    }

    // Lesson planning
    if (lowerQuestion.includes('lesson plan') || lowerQuestion.includes('lesson planning') || lowerQuestion.includes('plan lesson')) {
      return `Lesson Planning Tips:\n\n1. **Set Clear Objectives**: Define what students should learn by the end\n2. **Start with an Engaging Hook**: Capture attention at the beginning\n3. **Break into Steps**: Divide content into manageable chunks\n4. **Include Activities**: Mix lectures with hands-on activities\n5. **Assess Understanding**: Check comprehension throughout\n6. **Plan for Different Learners**: Accommodate various learning styles\n7. **Prepare Materials**: Gather all resources beforehand\n8. **Time Management**: Allocate time for each activity\n9. **Closure**: Summarize key points at the end\n10. **Reflection**: Note what worked and what to improve\n\nWhat subject or topic are you planning a lesson for?`;
    }

    // Student management
    if (lowerQuestion.includes('student management') || lowerQuestion.includes('manage students') || lowerQuestion.includes('classroom management')) {
      return `Student & Classroom Management Tips:\n\n1. **Establish Clear Rules**: Set expectations from day one\n2. **Be Consistent**: Apply rules fairly to all students\n3. **Build Relationships**: Get to know your students individually\n4. **Positive Reinforcement**: Acknowledge good behavior\n5. **Address Issues Promptly**: Don't let problems escalate\n6. **Use Non-Verbal Cues**: Eye contact, proximity, gestures\n7. **Engage All Students**: Keep everyone involved in activities\n8. **Create a Safe Environment**: Make students feel comfortable\n9. **Communicate with Parents**: Keep them informed about progress\n10. **Stay Calm**: Model the behavior you want to see\n\nYou can use the Attendance and Records tabs to track student progress!`;
    }

    // Grading and assessment
    if (lowerQuestion.includes('grading') || lowerQuestion.includes('assessment') || lowerQuestion.includes('evaluate') || lowerQuestion.includes('marks')) {
      return `Grading & Assessment Strategies:\n\n1. **Use Rubrics**: Clear criteria for evaluation\n2. **Grade Consistently**: Apply same standards to all\n3. **Provide Feedback**: Explain what students did well and what to improve\n4. **Mix Assessment Types**: Tests, projects, presentations, participation\n5. **Grade Promptly**: Return work in a timely manner\n6. **Be Fair**: Consider effort and improvement, not just final scores\n7. **Document Everything**: Keep records of all assessments\n8. **Communicate Expectations**: Students should know how they'll be graded\n\nYou can use the Exams and Tests tab to enter and manage student marks!`;
    }

    // Homework management
    if (lowerQuestion.includes('homework') || lowerQuestion.includes('assignment')) {
      return `Homework Management Tips:\n\n1. **Set Clear Instructions**: Students should know exactly what to do\n2. **Reasonable Amount**: Don't overload students\n3. **Check Regularly**: Review and provide feedback\n4. **Use Variety**: Mix different types of assignments\n5. **Connect to Lessons**: Homework should reinforce class learning\n6. **Set Deadlines**: Clear due dates help students plan\n7. **Provide Resources**: Give students what they need to complete work\n\nYou can use the Homework tab to create and manage assignments!`;
    }

    // Attendance tracking
    if (lowerQuestion.includes('attendance') || lowerQuestion.includes('absent') || lowerQuestion.includes('present')) {
      return `Attendance Management:\n\n1. **Take Attendance Daily**: Be consistent and timely\n2. **Track Patterns**: Notice if students are frequently absent\n3. **Follow Up**: Contact parents if attendance is concerning\n4. **Document Everything**: Keep accurate records\n5. **Be Understanding**: Sometimes absences are unavoidable\n6. **Encourage Attendance**: Make class engaging so students want to come\n\nYou can use the Attendance tab to mark and track student attendance!`;
    }

    // Teaching strategies
    if (lowerQuestion.includes('teaching') && (lowerQuestion.includes('strategy') || lowerQuestion.includes('method') || lowerQuestion.includes('technique'))) {
      return `Effective Teaching Strategies:\n\n1. **Active Learning**: Engage students in the learning process\n2. **Differentiated Instruction**: Adapt to different learning styles\n3. **Use Technology**: Incorporate digital tools when appropriate\n4. **Collaborative Learning**: Group work and peer teaching\n5. **Visual Aids**: Use diagrams, charts, and multimedia\n6. **Real-World Connections**: Relate content to students' lives\n7. **Questioning Techniques**: Ask thought-provoking questions\n8. **Scaffolding**: Build on prior knowledge gradually\n9. **Formative Assessment**: Check understanding throughout lessons\n10. **Reflection**: Encourage students to think about their learning\n\nWhat subject or grade level are you teaching?`;
    }

    // General greeting
    if (lowerQuestion.match(/^(hi|hello|hey|good morning|good afternoon|good evening)/)) {
      return `Hello! 👋 I'm your AI Assistant for Teachers!\n\nI can help you with:\n- 📚 Lesson planning and teaching strategies\n- 👥 Student and classroom management\n- 📊 Grading and assessment tips\n- 📝 Homework and assignment management\n- 📅 Attendance tracking\n- 💡 Educational best practices\n\nWhat would you like help with today?`;
    }

    // General educational response
    return `I'm here to help you with your teaching! 📚\n\nI can assist with:\n- Lesson planning and teaching strategies\n- Student and classroom management\n- Grading and assessment methods\n- Homework and assignment creation\n- Attendance tracking\n- Educational best practices\n\nCould you be more specific about what you need help with? For example:\n- "How do I plan an effective lesson?"\n- "What are good classroom management strategies?"\n- "How should I grade student work?"\n\nYou can also use the various tabs in this dashboard to manage your classes, students, and assignments!`;
  };


  // Save API key
  const handleSaveApiKey = () => {
    if (apiKeyInput.trim() && !apiKeyInput.includes('...')) {
      localStorage.setItem('gemini_api_key', apiKeyInput.trim());
      setHasApiKey(true);
      setShowApiKeySettings(false);
      setApiKeyInput('');
      alert('✅ API key saved successfully! You\'ll now get better AI responses.');
    }
  };

  // Remove API key
  const handleRemoveApiKey = () => {
    if (window.confirm('Are you sure you want to remove your API key?')) {
      localStorage.removeItem('gemini_api_key');
      setHasApiKey(false);
      setApiKeyInput('');
      setShowApiKeySettings(false);
      alert('API key removed. The assistant will still work with offline responses.');
    }
  };

  // Open API key settings
  const handleOpenApiKeySettings = () => {
    const storedKey = localStorage.getItem('gemini_api_key');
    if (storedKey) {
      setApiKeyInput(storedKey.substring(0, 10) + '...' + storedKey.substring(storedKey.length - 4));
    } else {
      setApiKeyInput('');
    }
    setShowApiKeySettings(true);
  };

  if (loading) {
    return (
      <div className="teacher-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!teacherData) {
    return (
      <div className="teacher-dashboard">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <p>Teacher data not found. Please login again.</p>
          <button onClick={() => navigate('/')} className="login-btn">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const homeworkClassesForSelectedDate = getClassesForDate(selectedHomeworkDate);
  const selectedHomeworkClass = getSelectedHomeworkClass();
  const canMarkSelectedAttendance = canMarkAttendanceForSelectedClass();

  return (
    <div className="teacher-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="teacher-info">
            <div className="school-name-header" style={{ color: '#FF8B3D', fontWeight: '900', fontSize: '20px' }}>
              {teacherData?.schools?.school_name || teacherData?.school_name || 'School'}
            </div>
            <div className="teacher-name-header">
              {teacherData.first_name} {teacherData.last_name}
            </div>
            <div className="teacher-role-header">Teacher</div>
          </div>
        </div>
        <div className="header-right">
          <button className="logout-btn-header desktop-only" onClick={handleLogout}>
            Logout
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info">
                <div className="user-avatar">
                  {teacherData.first_name?.charAt(0).toUpperCase()}{teacherData.last_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="mobile-user-name">{teacherData.first_name} {teacherData.last_name}</div>
                  <div className="mobile-user-role">Teacher</div>
                </div>
              </div>
              <button className="mobile-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                Dashboard
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'attendance' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('attendance');
                  setMobileMenuOpen(false);
                }}
              >
                Attendance
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'records' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('records');
                  setMobileMenuOpen(false);
                }}
              >
                Records
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'homework' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('homework');
                  setMobileMenuOpen(false);
                }}
              >
                Homework
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'exams' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('exams');
                  setMobileMenuOpen(false);
                }}
              >
                Exams and Tests
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('chat');
                  setMobileMenuOpen(false);
                }}
              >
                Chat
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="nav-tabs">
        <button
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          <span className="tab-text">Dashboard</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          <span className="tab-text">Schedule</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => handleTabChange('attendance')}
        >
          <span className="tab-text">Attendance</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'records' ? 'active' : ''}`}
          onClick={() => handleTabChange('records')}
        >
          <span className="tab-text">Records</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'homework' ? 'active' : ''}`}
          onClick={() => handleTabChange('homework')}
        >
          <span className="tab-text">Homework</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'exams' ? 'active' : ''}`}
          onClick={() => handleTabChange('exams')}
        >
          <span className="tab-text">Exams and Tests</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <span className="tab-text">Chat</span>
        </button>
      </div>

      <main className="dashboard-main">
        {activeTab === 'dashboard' && (
          <>
            <div className="welcome-section">
              <span className="welcome-emoji">👋</span>
              <h2 className="welcome-title">Welcome back, {teacherData.first_name}!</h2>
              <p className="welcome-subtitle">Here's your teacher dashboard with school information and notices.</p>
              <div className="current-date">
                <span className="calendar-icon">📅</span>
                <span>Today is {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
            </div>

        <div className="info-cards">
          <div className="info-card">
            <div className="card-icon">👨‍🏫</div>
            <div className="card-content">
              <h3>Teacher Information</h3>
              <p><strong>Name:</strong> {teacherData.first_name} {teacherData.last_name}</p>
              <p><strong>Email:</strong> {teacherData.email}</p>
              <p><strong>Phone:</strong> {teacherData.phone || 'Not specified'}</p>
              <p><strong>Date of Birth:</strong> {teacherData.date_of_birth ? formatDate(teacherData.date_of_birth) : 'Not specified'}</p>
              <p><strong>Address:</strong> {teacherData.address || 'Not specified'}</p>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">📚</div>
            <div className="card-content">
              <h3>Teaching Details</h3>
              <p><strong>Qualification:</strong> {teacherData.qualification || 'Not specified'}</p>
              <p><strong>Experience:</strong> {teacherData.experience || 'N/A'} years</p>
              <p><strong>Salary:</strong> {teacherData.salary ? (() => {
                const currency = teacherData.schools?.currency || 'PKR';
                const symbol = getCurrencySymbol(currency);
                return `${symbol} ${teacherData.salary.toLocaleString()}`;
              })() : 'Not specified'}</p>
              <p><strong>Joining Date:</strong> {teacherData.joining_date ? formatDate(teacherData.joining_date) : 'N/A'}</p>
              <p><strong>Status:</strong> {teacherData.is_active ? 'Active' : 'Inactive'}</p>
            </div>
          </div>

          <div className="info-card">
            <div className="card-icon">📖</div>
            <div className="card-content">
              <h3>Subjects & Classes</h3>
              <p><strong>Subjects:</strong> {teacherData.subjects && teacherData.subjects.length > 0 ? teacherData.subjects.filter(subject => subject && subject.trim() !== '').join(', ') : 'Not specified'}</p>
              <p><strong>Assigned Classes:</strong> {teacherData.class_ids && teacherData.class_ids.length > 0 ? 
                teacherData.class_ids.map(classId => classNames[classId] || `Class ${classId}`).join(', ') : 'Not assigned'}</p>
              <p><strong>Class Sections:</strong> {teacherData.class_sections && Object.keys(teacherData.class_sections).length > 0 ? 
                Object.entries(teacherData.class_sections).map(([classId, sections]) => 
                  `${classNames[classId] || `Class ${classId}`}: ${Array.isArray(sections) ? sections.join(', ') : sections}`
                ).join(' | ') : 'Not specified'}</p>
            </div>
          </div>

        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button 
              className="action-btn"
              onClick={() => handleTabChange('homework')}
            >
              📝 Create Assignments
            </button>
            <button 
              className="action-btn"
              onClick={() => handleTabChange('schedule')}
            >
              📅 View Schedule
            </button>
            <button 
              className="action-btn"
              onClick={() => handleTabChange('chat')}
            >
              💬 Contact Students
            </button>
          </div>
        </div>

        <div className="notices-section">
          <div className="notices-header">
            <h3>📢 School Notices</h3>
            <button 
              onClick={fetchTeacherNotices}
              className="refresh-btn"
              disabled={noticesLoading}
            >
              {noticesLoading ? '⏳' : '🔄'} Refresh
            </button>
          </div>
          {noticesLoading ? (
            <div className="notices-loading">
              <div className="loading-spinner">⏳</div>
              <p>Loading notices...</p>
            </div>
          ) : notices.length === 0 ? (
            <div className="no-notices">
              <div className="no-notices-icon">📢</div>
              <p>No notices available at the moment.</p>
              <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                Check console for debugging information.
              </p>
            </div>
          ) : (
            <div className="notices-list">
              {notices.map((notice) => (
                <div key={notice.id} className={`notice-card ${getPriorityClass(notice.priority)}`}>
                  <div className="notice-header">
                    <div className="notice-priority">
                      <span className="priority-icon">{getPriorityIcon(notice.priority)}</span>
                      <span className="priority-label">{getPriorityLabel(notice.priority)}</span>
                    </div>
                    <div className="notice-date">
                      📅 {formatDate(notice.created_at)}
                    </div>
                  </div>
                  <div className="notice-content">
                    <h4 className="notice-title">{notice.title}</h4>
                    <p className="notice-text">{notice.content}</p>
                    {notice.expiry_date && (
                      <div className="notice-expiry">
                        ⏰ Expires: {formatDate(notice.expiry_date)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {activeTab === 'schedule' && (
          <div className="schedule-section">
            {/* Page Header */}
            <div className="schedule-header-card">
              <div className="schedule-header-content">
                <div className="schedule-icon-title">
                  <svg className="schedule-header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h2 className="schedule-header-title">My Schedule</h2>
                </div>
                <p className="schedule-header-subtitle">Your teaching schedule for the current academic year</p>
              </div>
            </div>

            {scheduleLoading ? (
              <div className="schedule-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading your schedule...</p>
              </div>
              ) : Object.keys(schedulesBySemester).length > 0 ? (
                <>
                  {/* School Timings Card */}
                  {schoolTimings && (
                    <div className="school-timings-card">
                      <div className="header-row">
                        <span className="clock-icon">🕐</span>
                        <h3 className="title">School Timings</h3>
                      </div>
                      <div className="timing-info">
                        <div className="info-row">
                          <span className="label">School Hours:</span>
                          <span className="value">
                            {(() => {
                              const startTime = schoolTimings.start_time || '08:00';
                              const endTime = schoolTimings.end_time || '15:00';
                              return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                            })()}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="label">Period Duration:</span>
                          <span className="value">{schoolTimings.period_time || 45} minutes</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Semester Schedules */}
                  <div className="semester-schedules-container">
                    {Object.keys(schedulesBySemester).map((semester, semIndex) => {
                      const semesterData = schedulesBySemester[semester];
                      const semesterSchedule = semesterData.schedule;
                      
                      return (
                        <div key={semester} className={`semester-schedule-section ${semIndex === 0 ? 'first-semester' : ''}`}>
                          <div className="semester-header">
                            <h3 className="semester-title">
                              📚 {semesterData.semester_name || semester}
                              {semIndex === 0 && <span className="primary-badge">Primary</span>}
                            </h3>
                          </div>
                          
                          {/* Days Grid */}
                          <div className="schedule-days-grid">
                            {(() => {
                              // Get unique days that have scheduled classes
                              const daysWithSchedule = [...new Set(semesterSchedule.map(item => item.day))];
                              
                              // Sort days in order
                              const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                              const sortedDays = daysWithSchedule.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
                              
                              return sortedDays.map(day => {
                                const daySchedule = semesterSchedule.filter(item => item.day === day);
                                const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                                return (
                                  <div key={day} className="schedule-day-card">
                                    <div className="day-header-orange">
                                      <h3 className="day-name">{dayName}</h3>
                                      <span className="class-count-badge">{daySchedule.length} {daySchedule.length === 1 ? 'class' : 'classes'}</span>
                                    </div>
                                    <div className="day-content">
                                      {daySchedule.map((item, index) => {
                                        // Build class + section label without duplicating section name
                                        const rawClassName = classNames[item.class_id] || `Class ${item.class_id}`;
                                        const sectionName = item.section_name || item.section || 'A';
                                        const normalizedClassName = String(rawClassName);
                                        const normalizedSection = String(sectionName);
                                        const shouldAppendSection = normalizedSection &&
                                          !normalizedClassName.toLowerCase().endsWith(` - ${normalizedSection.toLowerCase()}`);
                                        const displayClassLabel = shouldAppendSection
                                          ? `${normalizedClassName} - ${normalizedSection}`
                                          : normalizedClassName;

                                        return (
                                          <div key={index} className="period-card">
                                            <div className="grade-info">
                                              {displayClassLabel}
                                            </div>
                                          <div className="subject-name">{item.subject_name}</div>
                                          <div className="time-row">
                                            <span className="clock-icon">🕐</span>
                                            <span className="time-text">{calculatePeriodTime(item.period)}</span>
                                          </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : teacherSchedule.length === 0 ? (
                <div className="no-schedule">
                  <div className="no-schedule-icon">📅</div>
                  <h3>No Schedule Found</h3>
                  <p>You don't have any classes scheduled yet.</p>
                  <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                    Contact your school administrator to set up your teaching schedule.
                  </p>
                </div>
              ) : (
                <>
                  {/* School Timings Card */}
                  {schoolTimings && (
                    <div className="school-timings-card">
                      <div className="header-row">
                        <span className="clock-icon">🕐</span>
                        <h3 className="title">School Timings</h3>
                      </div>
                      <div className="timing-info">
                        <div className="info-row">
                          <span className="label">School Hours:</span>
                          <span className="value">
                            {(() => {
                              const startTime = schoolTimings.start_time || '08:00';
                              const endTime = schoolTimings.end_time || '15:00';
                              return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                            })()}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="label">Period Duration:</span>
                          <span className="value">{schoolTimings.period_time || 45} minutes</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Days Grid */}
                  <div className="schedule-days-grid">
                {(() => {
                  // Get unique days that have scheduled classes
                  const daysWithSchedule = [...new Set(teacherSchedule.map(item => item.day))];
                  
                  // Sort days in order
                  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                  const sortedDays = daysWithSchedule.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
                  
                  return sortedDays.map(day => {
                    const daySchedule = teacherSchedule.filter(item => item.day === day);
                    const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                    return (
                      <div key={day} className="schedule-day-card">
                        <div className="day-header-orange">
                          <h3 className="day-name">{dayName}</h3>
                          <span className="class-count-badge">{daySchedule.length} {daySchedule.length === 1 ? 'class' : 'classes'}</span>
                        </div>
                        <div className="day-content">
                          {daySchedule.map((item, index) => (
                            <div key={index} className="period-card">
                              <div className="grade-info">
                                {classNames[item.class_id] || `Class ${item.class_id}`} - {item.section_name || item.section || 'A'}
                              </div>
                              <div className="subject-name">{item.subject_name}</div>
                              <div className="time-row">
                                <span className="clock-icon">🕐</span>
                                <span className="time-text">{calculatePeriodTime(item.period)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
                  </div>
                </>
              )}
            </div>
          )}

        {activeTab === 'attendance' && (
          <div className="attendance-section">
            <div className="attendance-header-card">
              <h2 className="attendance-title">Mark Attendance</h2>
              <p className="attendance-subtitle">Select a class and section to mark attendance for your students</p>
              <div className="attendance-date">
                <span className="calendar-icon">📅</span>
                <span className="date-value">{getTodayDateString()}</span>
              </div>
            </div>

            <div className="attendance-controls-card">
              <div className="attendance-controls">
                <div className="dropdown-group">
                  <label htmlFor="class-select">Class:</label>
                  <select 
                    id="class-select"
                    value={selectedClass} 
                    onChange={(e) => {
                      setSelectedClass(e.target.value);
                      setSelectedSection(''); // Reset section when class changes
                    }}
                    className="class-dropdown"
                  >
                    <option value="">Select Class</option>
                    {attendanceClasses.length === 0 && (
                      <option disabled>No class teacher assignments found</option>
                    )}
                    {attendanceClasses.map(cls => {
                      // Extract only class name, removing section if present (format: "ClassName - Section")
                      const classNameOnly = cls.class_name ? cls.class_name.split(' - ')[0].trim() : `Class ${cls.class_id}`;
                      return (
                        <option key={cls.class_id} value={cls.class_id}>
                          {classNameOnly}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="dropdown-group">
                  <label htmlFor="section-select">Section:</label>
                  <select 
                    id="section-select"
                    value={selectedSection} 
                    onChange={(e) => setSelectedSection(e.target.value)}
                    className="section-dropdown"
                    disabled={!selectedClass || loadingSections}
                  >
                    <option value="">Select Section</option>
                    {loadingSections ? (
                      <option disabled>Loading sections...</option>
                    ) : availableSections.length > 0 ? (
                      availableSections.map(section => (
                        <option key={section.id} value={String(section.id)}>
                          {section.section_name}
                        </option>
                      ))
                    ) : selectedClass ? (
                      <option disabled>No sections available</option>
                    ) : null}
                  </select>
                </div>

                <div className="dropdown-group">
                  <label htmlFor="date-select">Date:</label>
                  <input
                    id="date-select"
                    type="date"
                    value={selectedAttendanceDate}
                    onChange={(e) => setSelectedAttendanceDate(e.target.value)}
                    className="date-dropdown"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </div>

            {attendanceClasses.length === 0 && (
              <div className="select-prompt">
                <div className="select-prompt-icon">🔒</div>
                <h3>No Attendance Access</h3>
                <p>Only class teachers and assistant teachers can mark attendance. Please contact the school admin if this class should be assigned to you.</p>
              </div>
            )}

            {studentsLoading ? (
              <div className="students-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading students...</p>
              </div>
            ) : students.length === 0 && selectedClass && selectedSection ? (
              <div className="no-students">
                <div className="no-students-icon">👥</div>
                <h3>No Students Found</h3>
                <p>No students found for {attendanceClasses.find(cls => parseInt(cls.class_id) === parseInt(selectedClass))?.class_name} - Section {selectedSection}</p>
              </div>
            ) : students.length > 0 ? (
              <>
                <div className="students-grid">
                  {students.map((student, index) => (
                    <div key={student.id} className="student-card">
                      <div className="student-name">{student.first_name} {student.last_name}</div>
                      <div className="student-id-section">
                        <span className="student-id-text">ID: {getStudentDisplayId(student, student.id)}</span>
                      </div>
                      {attendanceData[student.id] && (
                        <div className="status-display">
                          <span className="status-label">Status:</span>
                          <span className={`status-value ${attendanceData[student.id]}`}>
                            {attendanceData[student.id].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="attendance-options">
                        <label className={`attendance-option present-option ${attendanceData[student.id] === 'present' ? 'selected' : ''}`}>
                          <input 
                            type="radio" 
                            name={`attendance_${student.id}`}
                            value="present"
                            checked={attendanceData[student.id] === 'present'}
                            onChange={() => markAttendance(student.id, 'present')}
                            disabled={attendanceLoading || !canMarkSelectedAttendance}
                            className="radio-input"
                          />
                          <span className="custom-radio"></span>
                          <span className="option-icon">✅</span>
                          <span className="option-text">Present</span>
                        </label>
                        
                        <label className={`attendance-option absent-option ${attendanceData[student.id] === 'absent' ? 'selected' : ''}`}>
                          <input 
                            type="radio" 
                            name={`attendance_${student.id}`}
                            value="absent"
                            checked={attendanceData[student.id] === 'absent'}
                            onChange={() => markAttendance(student.id, 'absent')}
                            disabled={attendanceLoading || !canMarkSelectedAttendance}
                            className="radio-input"
                          />
                          <span className="custom-radio"></span>
                          <span className="option-icon">❌</span>
                          <span className="option-text">Absent</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="save-attendance-container">
                  <button 
                    className="quick-action-btn mark-all-present-btn"
                    onClick={markAllPresent}
                    disabled={attendanceLoading || !canMarkSelectedAttendance}
                  >
                    Mark All Present
                  </button>
                  <button 
                    className="quick-action-btn mark-all-absent-btn"
                    onClick={markAllAbsent}
                    disabled={attendanceLoading || !canMarkSelectedAttendance}
                  >
                    Mark All Absent
                  </button>
                  <button 
                    className="submit-attendance-btn"
                    onClick={saveAllAttendance}
                    disabled={attendanceLoading || students.length === 0 || !canMarkSelectedAttendance}
                  >
                    {attendanceLoading ? 'Saving...' : 'Submit Attendance'}
                  </button>
                </div>
              </>
            ) : (
              <div className="select-prompt">
                <div className="select-prompt-icon">📋</div>
                <h3>Select Class and Section</h3>
                <p>Choose a class and section from the dropdowns above to view students</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'records' && (
          <div className="attendance-records-section">
            <div className="records-header-card">
              <div className="records-header-title">
                <h2 className="records-title">Attendance Records</h2>
              </div>
              <p className="records-subtitle">View attendance history for your assigned classes</p>
            </div>

            <div className="records-filters-card">
              <div className="records-controls">
                <div className="dropdown-group">
                  <label htmlFor="records-class-select">Class:</label>
                  <select 
                    id="records-class-select"
                    value={selectedClass} 
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="records-dropdown"
                  >
                    <option value="">Select Class</option>
                    {attendanceClasses.map(cls => (
                      <option key={cls.class_id} value={cls.class_id}>
                        {getClassDisplayName(cls.class_name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dropdown-group">
                  <label htmlFor="records-section-select">Section:</label>
                  <select 
                    id="records-section-select"
                    value={selectedSection} 
                    onChange={(e) => setSelectedSection(e.target.value)}
                    className="records-dropdown"
                  >
                    <option value="">Select Section</option>
                    {attendanceClasses.find(cls => cls.class_id == selectedClass)?.sections?.map(section => (
                      <option key={section} value={section}>
                        {section}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {selectedClass && selectedSection ? (
              <div className="records-content">
                <div className="month-navigation-bar">
                  <button 
                    className="month-nav-btn"
                    onClick={() => navigateMonth('prev')}
                  >
                    ← Previous
                  </button>
                  <div className="current-month-display">
                    {getMonthName(selectedMonth)} {selectedYear}
                  </div>
                  <button 
                    className="month-nav-btn"
                    onClick={() => navigateMonth('next')}
                  >
                    Next →
                  </button>
                </div>


                {recordsLoading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading attendance records...</p>
                  </div>
                ) : (
                  <>
                    {attendanceRecords.length > 0 ? (
                      <div className="records-display">
                        <div className="attendance-stats-grid">
                          <div className="stat-card stat-card-orange">
                            <div className="stat-number">{getAttendanceStats().totalDays}</div>
                            <div className="stat-label">Days with Records</div>
                          </div>
                          <div className="stat-card stat-card-green">
                            <div className="stat-number">{getAttendanceStats().totalRecords}</div>
                            <div className="stat-label">Total Records</div>
                          </div>
                          <div className="stat-card stat-card-green">
                            <div className="stat-number">{getAttendanceStats().presentCount}</div>
                            <div className="stat-label">Present</div>
                          </div>
                          <div className="stat-card stat-card-red">
                            <div className="stat-number">{getAttendanceStats().absentCount}</div>
                            <div className="stat-label">Absent</div>
                          </div>
                          <div className="stat-card stat-card-purple">
                            <div className="stat-number">{getAttendanceStats().attendanceRate}%</div>
                            <div className="stat-label">Attendance Rate</div>
                          </div>
                        </div>

                        <div className="attendance-list-container">
                          {Object.entries(groupRecordsByDate()).map(([date, records], dateIndex) => (
                            <div key={date} className="date-group-wrapper">
                              <div className="date-group-header">
                                <span className="date-text">
                                  {new Date(date).toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </span>
                                <span className="date-count-badge">{records.length} {records.length === 1 ? 'student' : 'students'}</span>
                              </div>
                              <div className="students-list-card">
                                {records.map((record, index) => {
                                  const isLast = index === records.length - 1;
                                  return (
                                    <div 
                                      key={`${record.student_id}-${record.attendance_date}`} 
                                      className={`student-row ${isLast ? 'last-row' : ''}`}
                                    >
                                      <div className={`status-icon-circle ${record.status}`}>
                                        {record.status === 'present' ? (
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        ) : (
                                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18M6 6L18 18" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        )}
                                      </div>
                                      <div className="student-info-column">
                                        <div className="student-name-text">
                                          {record.students?.first_name && record.students?.last_name 
                                            ? `${record.students.first_name} ${record.students.last_name}`
                                            : `Student ${record.student_id}`
                                          }
                                        </div>
                                        <div className="student-id-text">
                                          ID: {getStudentDisplayId(record.students, record.student_id)}
                                        </div>
                                      </div>
                                      <div className={`status-badge ${record.status}`}>
                                        {record.status === 'present' ? 'PRESENT' : 'ABSENT'}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="no-records">
                        <div className="no-records-icon">📅</div>
                        <h3>No Attendance Records</h3>
                        <p>No attendance records found for {getMonthName(selectedMonth)} {selectedYear}</p>
                        <p>Start marking attendance to see records here.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="select-prompt">
                <div className="select-prompt-icon">📊</div>
                <h3>Select Class and Section</h3>
                <p>Choose a class and section to view attendance records</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'homework' && (
          <div className="homework-section">
            <div className="homework-header">
              <h2>Homework Management</h2>
              <p>Add and manage homework for your assigned classes</p>
            </div>

            {/* Add Homework Form */}
            <div className="add-homework-form">
              <div className="homework-date-info">
                <h3>Add Homework</h3>
                <div className="today-info">
                  <span className="today-date">
                    📅 {new Date(`${selectedHomeworkDate}T00:00:00`).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
              
              <div className="homework-class-form">
                <form onSubmit={(e) => {
                  handleAddHomeworkForClass(e, {
                    class_id: newHomework.class_id,
                    section_name: newHomework.section_name,
                    subject: newHomework.subject,
                    title: newHomework.title || `${newHomework.subject} Homework`,
                    description: newHomework.description,
                    assigned_date: selectedHomeworkDate,
                    due_date: newHomework.due_date || addDaysToISODateString(selectedHomeworkDate, 7)
                  });
                }}>
                  <div className="homework-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div className="homework-form-group">
                      <label htmlFor="homework-date">Homework Date</label>
                      <input
                        id="homework-date"
                        type="date"
                        value={selectedHomeworkDate}
                        min={getTodayISODateString()}
                        onChange={(e) => handleHomeworkDateChange(e.target.value)}
                        className="homework-input"
                        required
                      />
                    </div>

                    <div className="homework-form-group">
                      <label htmlFor="homework-class">Class & Subject</label>
                      <select
                        id="homework-class"
                        value={newHomework.class_key}
                        onChange={(e) => handleHomeworkClassChange(e.target.value)}
                        className="homework-input"
                        required
                      >
                        <option value="">Select class and subject</option>
                        {homeworkClassesForSelectedDate.map((cls, index) => (
                          <option key={`${getHomeworkClassKey(cls)}-${index}`} value={getHomeworkClassKey(cls)}>
                            {cls.class_name} - {cls.section_name} | {cls.subject}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="homework-form-group">
                      <label htmlFor="homework-due-date">Due Date</label>
                      <input
                        id="homework-due-date"
                        type="date"
                        value={newHomework.due_date || addDaysToISODateString(selectedHomeworkDate, 7)}
                        min={selectedHomeworkDate}
                        onChange={(e) => setNewHomework(prev => ({ ...prev, due_date: e.target.value }))}
                        className="homework-input"
                        required
                      />
                    </div>
                  </div>

                  {selectedHomeworkClass && (
                    <div className="class-subject-header">
                      <h4>📚 Class {selectedHomeworkClass.class_name} - {selectedHomeworkClass.section_name}</h4>
                      <h5>📖 {selectedHomeworkClass.subject}</h5>
                    </div>
                  )}

                  <textarea 
                    name="homework-text"
                    value={newHomework.description}
                    onChange={(e) => setNewHomework(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={selectedHomeworkClass ? `Write homework for ${selectedHomeworkClass.subject}...` : 'Select a class first, then write homework...'}
                    className="homework-textarea"
                    rows="6"
                    required
                  />
                  
                  <div className="homework-file-upload">
                    <label htmlFor="homework-file-selected" className="file-upload-label">
                      📎 Attach File/Image
                    </label>
                    <input
                      type="file"
                      id="homework-file-selected"
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={(e) => {
                        if (!selectedHomeworkClass) return;
                        handleHomeworkFileChange(e, selectedHomeworkClass.class_id, selectedHomeworkClass.section_name, selectedHomeworkClass.subject);
                      }}
                      className="file-input"
                      disabled={!selectedHomeworkClass}
                    />
                    {selectedHomeworkClass && homeworkFiles[getHomeworkClassKey(selectedHomeworkClass)] && (
                      <span className="file-name">
                        📄 {homeworkFiles[getHomeworkClassKey(selectedHomeworkClass)].name}
                      </span>
                    )}
                  </div>
                  
                  <button type="submit" className="add-homework-btn" disabled={!selectedHomeworkClass}>
                    Add Homework
                  </button>
                </form>
              </div>

              {homeworkClassesForSelectedDate.length === 0 && (
                <div className="no-classes-today">
                  <div className="no-classes-icon">📅</div>
                  <h4>No Classes on Selected Date</h4>
                  <p>You don't have any classes scheduled for this date. Choose another present or future date.</p>
                </div>
              )}
            </div>

            {/* Homework List */}
            <div className="homework-list">
              <h3>Recent Homework Assignments</h3>
              
              {homeworkLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner">⏳</div>
                  <p>Loading homework...</p>
                </div>
              ) : homeworkData.length === 0 ? (
                <div className="no-homework">
                  <div className="no-homework-icon"></div>
                  <h3>No Homework Assigned</h3>
                  <p>You haven't assigned any homework yet.</p>
                  <p>Use the form above to add homework for your classes.</p>
                </div>
              ) : (
                <div className="homework-grid">
                  {homeworkData.map(homework => (
                    <div key={homework.id} className="homework-card">
                      <div className="homework-header-card">
                        <h4>{homework.title}</h4>
                        <div className="homework-actions">
                          <button 
                            className="view-completion-btn"
                            onClick={() => handleViewHomeworkCompletion(homework)}
                            title="View Students & Mark Completion"
                          >
                            👥
                          </button>
                          <button 
                            className="edit-homework-btn"
                            onClick={() => handleEditHomework(homework)}
                            title="Edit Homework"
                          >
                            ✏️
                          </button>
                          <button 
                            className="delete-homework-btn"
                            onClick={() => handleDeleteHomework(homework.id)}
                            title="Delete Homework"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      {editingHomework === homework.id ? (
                        <div className="homework-edit-form">
                          <div className="edit-form-group">
                            <label>Description:</label>
                            <textarea
                              value={editHomeworkData.description}
                              onChange={(e) => setEditHomeworkData(prev => ({ ...prev, description: e.target.value }))}
                              className="edit-textarea"
                              rows="4"
                            />
                          </div>
                          
                          <div className="edit-form-group">
                            <label>Due Date:</label>
                            <input
                              type="date"
                              value={editHomeworkData.due_date}
                              onChange={(e) => setEditHomeworkData(prev => ({ ...prev, due_date: e.target.value }))}
                              className="edit-input"
                            />
                          </div>
                          
                          <div className="edit-form-actions">
                            <button 
                              className="save-edit-btn"
                              onClick={() => handleSaveEdit(homework.id)}
                            >
                              💾 Save
                            </button>
                            <button 
                              className="cancel-edit-btn"
                              onClick={handleCancelEdit}
                            >
                              ❌ Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="homework-details">
                          <div className="homework-info">
                            <span className="homework-class">
                              📚 {classNames[homework.class_id] || `Class ${homework.class_id}`} - {homework.section_name}
                            </span>
                            <span className="homework-subject">
                              📖 {homework.subject}
                            </span>
                          </div>
                          
                          <div className="homework-description">
                            <p>{homework.description}</p>
                          </div>
                          
                          {/* Display attachments if any */}
                          {homework.attachments && Array.isArray(homework.attachments) && homework.attachments.length > 0 && (
                            <div className="homework-attachments">
                              <h5>📎 Attachments:</h5>
                              <div className="attachments-list">
                                {homework.attachments.map((attachment, idx) => (
                                  <div key={idx} className="attachment-item">
                                    {attachment.type && attachment.type.startsWith('image/') ? (
                                      <div className="attachment-image">
                                        <img 
                                          src={attachment.url || ''} 
                                          alt={attachment.name}
                                          onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                              const url = await getAttachmentUrl(attachment);
                                              if (url) {
                                                window.open(url, '_blank');
                                              } else {
                                                alert('Unable to load image. The file may have been deleted or the bucket is not accessible.');
                                              }
                                            } catch (error) {
                                              console.error('Error opening image:', error);
                                              alert('Unable to load image. Please try again later.');
                                            }
                                          }}
                                          onError={async (e) => {
                                            console.error('Image load error for:', attachment.url);
                                            // Try to get signed URL as fallback if we have a path
                                            if (attachment.path) {
                                              const bucketName = attachment.bucket || 'student-images';
                                              try {
                                                // Try signed URL first
                                                const { data, error } = await supabase.storage
                                                  .from(bucketName)
                                                  .createSignedUrl(attachment.path, 3600);
                                                if (!error && data) {
                                                  e.target.src = data.signedUrl;
                                                  return;
                                                }
                                              } catch (err) {
                                                console.error('Error getting signed URL:', err);
                                              }
                                              
                                              // Try public URL as fallback
                                              try {
                                                const { data: { publicUrl } } = supabase.storage
                                                  .from(bucketName)
                                                  .getPublicUrl(attachment.path);
                                                e.target.src = publicUrl;
                                              } catch (pubErr) {
                                                console.error('Error getting public URL:', pubErr);
                                                e.target.style.display = 'none';
                                                e.target.alt = 'Image not available - Bucket may not exist. Please contact administrator.';
                                              }
                                            } else {
                                              e.target.style.display = 'none';
                                              e.target.alt = 'Image not available';
                                            }
                                          }}
                                          style={{ cursor: 'pointer', maxWidth: '200px', maxHeight: '200px', borderRadius: '8px' }}
                                        />
                                        <a 
                                          href={attachment.url || '#'} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="attachment-link"
                                          onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                              const url = await getAttachmentUrl(attachment);
                                              if (url) {
                                                window.open(url, '_blank');
                                              } else {
                                                alert('Unable to load image. The file may have been deleted or the bucket is not accessible.');
                                              }
                                            } catch (error) {
                                              console.error('Error opening image:', error);
                                              alert('Unable to load image. Please try again later.');
                                            }
                                          }}
                                        >
                                          {attachment.name}
                                        </a>
                                      </div>
                                    ) : (
                                      <a 
                                        href={attachment.url || '#'} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          try {
                                            const url = await getAttachmentUrl(attachment);
                                            if (url) {
                                              window.open(url, '_blank');
                                            } else {
                                              alert('Unable to load file. The file may have been deleted or the bucket is not accessible.');
                                            }
                                          } catch (error) {
                                            console.error('Error opening file:', error);
                                            alert('Unable to load file. Please try again later.');
                                          }
                                        }}
                                      >
                                        📄 {attachment.name} ({(attachment.size / 1024).toFixed(2)} KB)
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="homework-dates">
                            <span className="assigned-date">
                              📅 Assigned: {new Date(homework.assigned_date).toLocaleDateString()}
                            </span>
                            <span className="due-date">
                              ⏰ Due: {new Date(homework.due_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'exams' && (
          <div className="exams-section">
            <div className="exams-header">
              <div className="exams-header-icon">📋</div>
              <h2 className="exams-header-title">Exams and Tests</h2>
              <p className="exams-header-subtitle">View exams and tests for your assigned subjects</p>
            </div>

            {examsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner">⏳</div>
                <p>Loading exams...</p>
              </div>
            ) : teacherExams.length === 0 ? (
              <div className="no-exams">
                <div className="no-exams-icon">📅</div>
                <h3>No Exams Scheduled</h3>
                <p>There are no exams or tests scheduled for your assigned subjects yet.</p>
              </div>
            ) : (
              <div className="exams-grid">
                {teacherExams.map(exam => (
                  <div key={exam.id} className="exam-card">
                    <div className="exam-card-header">
                      <span className="exam-type-badge">
                        {exam.type === 'monthly_test' ? 'MONTHLY TEST' : 
                         exam.type === 'exam' ? 'EXAM' : 
                         exam.type === 'holiday' ? 'HOLIDAY' : 'TEST'}
                      </span>
                    </div>
                    
                    <div className="exam-card-content">
                      <h4 className="exam-title">{exam.title}</h4>
                      
                      <div className="exam-dates">
                        <span className="exam-date">
                          {exam.start_date === exam.end_date 
                            ? new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : `${new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(exam.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                          }
                        </span>
                      </div>

                      {(exam.class_id || exam.section_name) && (
                        <p className="exam-assigned">
                          <strong>Class:</strong> {classNames[exam.class_id] || `Class ${exam.class_id}`}
                          {exam.section_name && ` - Section ${exam.section_name}`}
                        </p>
                      )}

                      {exam.description && (
                        <p className="exam-description">{exam.description}</p>
                      )}

                      {exam.relevantSubjects && exam.relevantSubjects.length > 0 && (
                        <div className="exam-subjects">
                          <h5 className="exam-subjects-title">Your Subjects:</h5>
                          {exam.relevantSubjects.map((subject, index) => (
                            <div key={index} className="exam-subject-item">
                              <div className="subject-info">
                                <span className="subject-name">📖 {subject.name}</span>
                                <span className="subject-datetime">
                                  {new Date(subject.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} • {subject.time || 'TBA'}
                                </span>
                              </div>
                              <button
                                className="add-marks-btn"
                                onClick={() => handleAddMarks(exam, subject)}
                                title="Add Marks/Grades"
                              >
                                📝 Add Marks
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Marks Entry Section */}
            {showMarksSection && selectedExam && (
              <div className="marks-entry-section" ref={marksEntrySectionRef} id="marks-entry-section">
                <div className="marks-entry-header">
                  <div>
                    <h3>📝 Add Marks/Grades</h3>
                    <p>
                      <strong>Exam:</strong> {selectedExam.exam.title} | 
                      <strong> Subject:</strong> {selectedExam.subject.name} | 
                      <strong> Class:</strong> {classNames[selectedExam.exam.class_id] || `Class ${selectedExam.exam.class_id}`}
                      {selectedExam.exam.section_name && ` - Section ${selectedExam.exam.section_name}`}
                    </p>
                    <p className="marks-instruction">
                      You can add <strong>marks</strong> (obtained/total) OR <strong>grade</strong> OR both. At least one is required.
                    </p>
                  </div>
                  <button
                    className="close-marks-btn"
                    onClick={() => {
                      setShowMarksSection(false);
                      setSelectedExam(null);
                      setMarksData({});
                      setGlobalTotalMarks('');
                    }}
                  >
                    ✕ Close
                  </button>
                </div>

                {/* Global Total Marks Input */}
                <div className="global-total-marks-section">
                  <div className="global-total-marks-box">
                    <label htmlFor="global-total-marks" className="global-total-marks-label">
                      📊 Total Marks (for all students):
                    </label>
                    <div className="global-total-marks-input-group">
                      <input
                        type="number"
                        id="global-total-marks"
                        step="0.01"
                        min="0"
                        className="global-total-marks-input"
                        placeholder="Enter total marks (e.g., 100)"
                        value={globalTotalMarks}
                        onChange={(e) => {
                          const value = e.target.value;
                          setGlobalTotalMarks(value);
                          // Auto-apply to all students who don't have individual total marks set
                          if (value) {
                            const updatedMarks = {};
                            examStudents.forEach(student => {
                              const currentData = marksData[student.id] || {};
                              // Only apply if student doesn't have individual total marks
                              if (!currentData.total_marks) {
                                updatedMarks[student.id] = {
                                  ...currentData,
                                  total_marks: value
                                };
                              }
                            });
                            if (Object.keys(updatedMarks).length > 0) {
                              setMarksData(prev => ({ ...prev, ...updatedMarks }));
                            }
                          }
                        }}
                      />
                      <button
                        className="apply-total-marks-btn"
                        onClick={() => {
                          if (globalTotalMarks) {
                            const updatedMarks = {};
                            examStudents.forEach(student => {
                              updatedMarks[student.id] = {
                                ...marksData[student.id] || {},
                                total_marks: globalTotalMarks
                              };
                            });
                            setMarksData(prev => ({ ...prev, ...updatedMarks }));
                            setTotalMarksApplied(globalTotalMarks);
                            setShowTotalMarksPopup(true);
                            // Auto-hide after 3 seconds
                            setTimeout(() => {
                              setShowTotalMarksPopup(false);
                            }, 3000);
                          }
                        }}
                      >
                        ✓ Apply to All
                      </button>
                    </div>
                    <p className="global-total-marks-hint">
                      Set total marks once and it will be applied to all students. Then enter obtained marks for each student below.
                    </p>
                  </div>
                </div>

                {examStudentsLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading students...</p>
                  </div>
                ) : examStudents.length === 0 ? (
                  <div className="no-students">
                    <p>No students found for this exam.</p>
                  </div>
                ) : (
                  <div className="marks-table-container">
                    <table className="marks-table">
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th>Student ID</th>
                          <th>Obtained Marks</th>
                          <th>Total Marks</th>
                          <th>Grade</th>
                          <th>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {examStudents.map(student => (
                          <tr key={student.id}>
                            <td className="student-name-cell">
                              {student.first_name} {student.last_name}
                            </td>
                            <td className="student-id-cell">
                              {student.student_id_no || student.student_id || student.id}
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="marks-input"
                                placeholder="Enter obtained marks"
                                value={marksData[student.id]?.obtained_marks || ''}
                                onChange={(e) => handleMarksChange(student.id, 'obtained_marks', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="marks-input total-marks-cell"
                                placeholder={globalTotalMarks || "Total"}
                                value={marksData[student.id]?.total_marks || (globalTotalMarks && !marksData[student.id]?.total_marks ? globalTotalMarks : '')}
                                onChange={(e) => handleMarksChange(student.id, 'total_marks', e.target.value)}
                                title={globalTotalMarks ? `Global total: ${globalTotalMarks} (you can override for individual students)` : "Enter total marks or use global total above"}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="grade-input"
                                placeholder="A+, A, B+, etc."
                                maxLength="10"
                                value={marksData[student.id]?.grade || ''}
                                onChange={(e) => handleMarksChange(student.id, 'grade', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="remarks-input"
                                placeholder="Optional"
                                value={marksData[student.id]?.remarks || ''}
                                onChange={(e) => handleMarksChange(student.id, 'remarks', e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="marks-actions">
                      <button
                        className="save-marks-btn"
                        onClick={handleSaveMarks}
                        disabled={savingMarks}
                      >
                        {savingMarks ? '💾 Saving...' : '💾 Save Marks/Grades'}
                      </button>
                      <button
                        className="cancel-marks-btn"
                    onClick={() => {
                      setShowMarksSection(false);
                      setSelectedExam(null);
                      setMarksData({});
                      setGlobalTotalMarks('');
                    }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="chat-section-modern">
            {/* Page Header */}
            <div className="chat-page-header">
              <div className="chat-header-icon">💬</div>
              <h1 className="chat-page-title">Chat</h1>
              <div className="chat-view-toggle">
                <button
                  className={`toggle-btn ${chatViewMode === 'students' ? 'active' : ''}`}
                  onClick={() => {
                    // Clean up group chat channel
                    if (window.currentGroupChatChannel) {
                      supabase.removeChannel(window.currentGroupChatChannel);
                      window.currentGroupChatChannel = null;
                    }
                    setChatViewMode('students');
                    setSelectedChatGroup(null);
                    setSelectedChatStudent(null);
                  }}
                >
                  Students
                </button>
                <button
                  className={`toggle-btn ${chatViewMode === 'groups' ? 'active' : ''}`}
                  onClick={() => {
                    // Clean up individual chat channel
                    if (window.currentChatChannel) {
                      supabase.removeChannel(window.currentChatChannel);
                      window.currentChatChannel = null;
                    }
                    setChatViewMode('groups');
                    setSelectedChatStudent(null);
                    setSelectedChatGroup(null);
                  }}
                >
                  Groups
                </button>
              </div>
            </div>

            {chatViewMode === 'students' ? (
              <>
                {chatStudentsLoading ? (
                  <div className="chat-loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading students...</p>
                  </div>
                ) : chatStudents.length === 0 ? (
                  <div className="chat-no-teachers">
                    <div className="no-teachers-icon">👨‍🎓</div>
                    <h3>No Students Found</h3>
                    <p>You don't have any active conversations with students yet.</p>
                  </div>
                ) : (
                  <div className="chat-main-container">
                {/* Left Sidebar - Students List */}
                <div className={`chat-sidebar ${!showChatSidebar ? 'hidden-mobile' : ''}`}>
                  <div className="chat-sidebar-header">
                    <h3 className="chat-sidebar-title">Your Students</h3>
                  </div>
                  <div className="chat-search-container">
                    <input
                      type="text"
                      className="chat-search-input"
                      placeholder="Search students..."
                      value={studentSearchQuery}
                      onChange={(e) => setStudentSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="chat-teachers-list">
                    {chatStudents
                      .filter(student => {
                        if (!studentSearchQuery.trim()) return true;
                        const query = studentSearchQuery.toLowerCase();
                        const name = `${student.first_name} ${student.last_name}`.toLowerCase();
                        return name.includes(query);
                      })
                      .map(student => (
                        <div
                          key={student.id}
                          className={`chat-teacher-card ${selectedChatStudent?.id === student.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedChatStudent(student);
                            setShowChatSidebar(false);
                          }}
                        >
                          <div className="chat-teacher-avatar-wrapper">
                            <div className="chat-teacher-avatar">{student.isAdmin ? '🏫' : '👨‍🎓'}</div>
                            <div className="chat-online-indicator"></div>
                          </div>
                          <div className="chat-teacher-info">
                            <h4 className="chat-teacher-name">{student.first_name} {student.last_name}</h4>
                            {student.isAdmin ? (
                              <p className="chat-teacher-qualification">Administration</p>
                            ) : student.class_id && (
                              <p className="chat-teacher-qualification">Class: {student.class_name || student.class_id} - Section: {student.section_id || 'N/A'}</p>
                            )}
                          </div>
                          {unreadCounts[student.id] > 0 && (
                            <div className="chat-unread-badge">{unreadCounts[student.id] > 99 ? '99+' : unreadCounts[student.id]}</div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Right Chat Area */}
                {selectedChatStudent ? (
                  <div className="chat-window-modern">
                    <div className="chat-window-header-modern">
                      <button 
                        className="chat-back-button"
                        onClick={() => {
                          setShowChatSidebar(true);
                          setSelectedChatStudent(null);
                        }}
                      >
                        ←
                      </button>
                      <div className="chat-header-avatar-wrapper">
                        <div className="chat-header-avatar">{selectedChatStudent.isAdmin ? '🏫' : '👨‍🎓'}</div>
                        <div className="chat-online-indicator-small"></div>
                      </div>
                      <div className="chat-header-teacher-info">
                        <h3 className="chat-header-teacher-name">{selectedChatStudent.first_name} {selectedChatStudent.last_name}</h3>
                        {selectedChatStudent.isAdmin && (
                          <p className="chat-header-teacher-subject">Administration</p>
                        )}
                      </div>
                    </div>

                    {chatLoading ? (
                      <div className="chat-loading-state">
                        <div className="loading-spinner">⏳</div>
                        <p>Loading chat...</p>
                      </div>
                    ) : (
                      <>
                        <div className="chat-messages-area">
                          {chatMessages.length === 0 ? (
                            <div className="chat-no-messages">
                              <p>No messages yet. Start the conversation!</p>
                            </div>
                          ) : (
                            <>
                              {chatMessages.map(message => {
                                // Check if message is unread
                                const lastReadKey = selectedChatStudent.isAdmin 
                                  ? `lastRead_${teacherData.id}_admin`
                                  : `lastRead_${teacherData.id}_${selectedChatStudent.id}`;
                                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                                const isUnread = (message.sender_type === 'student' || message.sender_type === 'school') && 
                                  (!lastReadTimestamp || message.created_at > lastReadTimestamp);
                                
                                // Mark message as read when viewed
                                if (isUnread && (message.sender_type === 'student' || message.sender_type === 'school')) {
                                  markMessageAsRead(message.id, message);
                                }

                                const messageStatus = getMessageStatus(message);
                                
                                return (
                                  <div
                                    key={message.id}
                                    className={`chat-message ${message.sender_type === 'teacher' && message.sender_id === teacherData.id.toString() ? 'sent' : 'received'} ${isUnread ? 'has-unread-indicator' : ''}`}
                                  >
                                    <div className="chat-message-content">
                                      {isUnread && <div className="unread-indicator-circle"></div>}
                                      <div className="chat-message-sender">
                                        {message.sender_type === 'teacher' ? 'You' : selectedChatStudent.isAdmin ? 'School Admin' : selectedChatStudent.first_name}
                                      </div>
                                      {message.content && <div className="chat-message-text">{message.content}</div>}
                                      {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                        <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {message.media_urls.map((media, idx) => (
                                            <div key={idx}>
                                              {media.type && media.type.startsWith('image/') ? (
                                                <img
                                                  src={media.url}
                                                  alt={media.name || 'Image'}
                                                  style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                                  onClick={() => window.open(media.url, '_blank')}
                                                />
                                              ) : (
                                                <a
                                                  href={media.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    background: '#F3F4F6',
                                                    borderRadius: '8px',
                                                    textDecoration: 'none',
                                                    color: '#1F2937',
                                                    maxWidth: '300px'
                                                  }}
                                                >
                                                  <span>📄</span>
                                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {media.name || 'Document'}
                                                  </span>
                                                </a>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <div className="chat-message-time">
                                          {new Date(message.created_at).toLocaleString()}
                                        </div>
                                        {messageStatus && (
                                          <span style={{ 
                                            color: messageStatus.color, 
                                            fontSize: '14px', 
                                            marginLeft: '8px',
                                            fontWeight: '500'
                                          }} title={messageStatus.status === 'read' ? 'Read' : 'Delivered'}>
                                            {messageStatus.icon}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              <div ref={chatMessagesEndRef} />
                            </>
                          )}
                        </div>

                        <div className="chat-input-area-modern">
                          {selectedFiles.length > 0 && (
                            <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {selectedFiles.map((file, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                                  <span>{file.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                              id="chat-file-input-teacher"
                            />
                            <label
                              htmlFor="chat-file-input-teacher"
                              style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                              title="Attach file"
                            >
                              📎
                            </label>
                            <input
                              type="text"
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  sendMessage(e);
                                }
                              }}
                              placeholder="Type your message..."
                              className="chat-input-modern"
                              style={{ flex: 1, minWidth: 0, width: '100%' }}
                            />
                            <button
                              type="button"
                              onClick={(e) => sendMessage(e)}
                              disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                              className="chat-send-button"
                              title="Send message"
                            >
                              {uploadingFiles ? '⏳' : '➤'}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="chat-window-modern">
                    <div className="chat-empty-state">
                      <div className="chat-empty-icon">💬</div>
                      <h3 className="chat-empty-title">Select a Student</h3>
                      <p className="chat-empty-message">Choose a student from the list to view and reply to their messages</p>
                    </div>
                  </div>
                )}
                </div>
                )}
                </>
              ) : (
              <>
                {loadingGroups ? (
                  <div className="chat-loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading chat groups...</p>
                  </div>
                ) : chatGroups.length === 0 ? (
                  <div className="chat-no-teachers">
                    <div className="no-teachers-icon">👥</div>
                    <h3>No Chat Groups</h3>
                    <p>You are not a member of any chat groups yet.</p>
                  </div>
                ) : (
                  <div className="chat-main-container">
                    {/* Left Sidebar - Groups List */}
                    <div className={`chat-sidebar ${!showChatSidebar ? 'hidden-mobile' : ''}`}>
                      <div className="chat-sidebar-header">
                        <h3 className="chat-sidebar-title">Chat Groups</h3>
                      </div>
                      <div className="chat-search-container">
                        <input
                          type="text"
                          className="chat-search-input"
                          placeholder="Search groups..."
                          value={studentSearchQuery}
                          onChange={(e) => setStudentSearchQuery(e.target.value)}
                        />
                      </div>
                      <div className="chat-teachers-list">
                        {chatGroups
                          .filter(group => {
                            if (!studentSearchQuery.trim()) return true;
                            const query = studentSearchQuery.toLowerCase();
                            return group.name.toLowerCase().includes(query) ||
                                   (group.description && group.description.toLowerCase().includes(query));
                          })
                          .map(group => (
                            <div
                              key={group.id}
                              className={`chat-teacher-card ${selectedChatGroup?.id === group.id ? 'selected' : ''}`}
                              onClick={() => {
                                loadGroupChat(group);
                                setShowChatSidebar(false);
                              }}
                            >
                              <div className="chat-teacher-avatar-wrapper">
                                <div className="chat-teacher-avatar">👥</div>
                                <div className="chat-online-indicator"></div>
                              </div>
                              <div className="chat-teacher-info">
                                <h4 className="chat-teacher-name">{group.name}</h4>
                                {group.lastMessage && (
                                  <p className="chat-teacher-subject">
                                    {group.lastMessageSender?.type === 'teacher' && group.lastMessageSender.id === teacherData.id.toString() 
                                      ? 'You: ' 
                                      : group.lastMessageSender?.type === 'student' 
                                        ? 'Student: ' 
                                        : group.lastMessageSender?.type === 'teacher'
                                          ? 'Teacher: '
                                          : ''}
                                    {group.lastMessage.length > 30 ? group.lastMessage.substring(0, 30) + '...' : group.lastMessage}
                                  </p>
                                )}
                                {group.memberCount > 0 && (
                                  <p className="chat-teacher-subject" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Right Chat Area - Group Chat */}
                    {selectedChatGroup ? (
                      <div className="chat-window-modern">
                        <div className="chat-window-header-modern">
                          <button 
                            className="chat-back-button"
                            onClick={() => {
                              // Clean up group chat channel
                              if (window.currentGroupChatChannel) {
                                supabase.removeChannel(window.currentGroupChatChannel);
                                window.currentGroupChatChannel = null;
                              }
                              setShowChatSidebar(true);
                              setSelectedChatGroup(null);
                            }}
                          >
                            ←
                          </button>
                          <div className="chat-header-avatar-wrapper">
                            <div className="chat-header-avatar">👥</div>
                            <div className="chat-online-indicator-small"></div>
                          </div>
                          <div className="chat-header-teacher-info">
                            <h3 className="chat-header-teacher-name">{selectedChatGroup.name}</h3>
                            {selectedChatGroup.description && (
                              <p className="chat-header-teacher-subject">{selectedChatGroup.description}</p>
                            )}
                            {selectedChatGroup.memberCount > 0 && (
                              <p className="chat-header-teacher-subject" style={{ fontSize: '0.75rem' }}>
                                {selectedChatGroup.memberCount} member{selectedChatGroup.memberCount !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        {groupChatLoading ? (
                          <div className="chat-loading-state">
                            <div className="loading-spinner">⏳</div>
                            <p>Loading chat...</p>
                          </div>
                        ) : (
                          <>
                            <div className="chat-messages-area">
                              {groupChatMessages.length === 0 ? (
                                <div className="chat-no-messages">
                                  <p>No messages yet. Start the conversation!</p>
                                </div>
                              ) : (
                                <>
                                  {groupChatMessages.map(message => {
                                    const isOwnMessage = message.sender_type === 'teacher' && message.sender_id === teacherData.id.toString();
                                    
                                    // Mark message as read when viewed (for received messages)
                                    if (!isOwnMessage) {
                                      markMessageAsRead(message.id, message);
                                    }

                                    // Get read count for group messages
                                    const readBy = message.read_by || [];
                                    const readCount = readBy.length;
                                    const totalMembers = selectedChatGroup?.memberCount || 1;
                                    const messageStatus = isOwnMessage ? {
                                      status: readCount > 0 ? 'read' : 'delivered',
                                      icon: readCount > 0 ? '✓✓' : '✓✓',
                                      color: readCount > 0 ? '#4F46E5' : '#9CA3AF',
                                      readCount: readCount,
                                      totalMembers: totalMembers
                                    } : null;
                                    
                                    return (
                                      <div
                                        key={message.id}
                                        className={`chat-message ${isOwnMessage ? 'sent' : 'received'}`}
                                      >
                                        <div className="chat-message-content">
                                          <div className="chat-message-sender">
                                            {message.senderName || 'Unknown'}
                                          </div>
                                          {message.content && <div className="chat-message-text">{message.content}</div>}
                                          {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                            <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                              {message.media_urls.map((media, idx) => (
                                                <div key={idx}>
                                                  {media.type && media.type.startsWith('image/') ? (
                                                    <img
                                                      src={media.url}
                                                      alt={media.name || 'Image'}
                                                      style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                                      onClick={() => window.open(media.url, '_blank')}
                                                    />
                                                  ) : (
                                                    <a
                                                      href={media.url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px 12px',
                                                        background: '#F3F4F6',
                                                        borderRadius: '8px',
                                                        textDecoration: 'none',
                                                        color: '#1F2937',
                                                        maxWidth: '300px'
                                                      }}
                                                    >
                                                      <span>📄</span>
                                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {media.name || 'Document'}
                                                      </span>
                                                    </a>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                                            <div className="chat-message-time">
                                              {new Date(message.created_at).toLocaleString()}
                                            </div>
                                            {messageStatus && (
                                              <span style={{ 
                                                color: messageStatus.color, 
                                                fontSize: '14px', 
                                                marginLeft: '8px',
                                                fontWeight: '500'
                                              }} title={messageStatus.readCount > 0 ? `Read by ${messageStatus.readCount}/${messageStatus.totalMembers}` : 'Delivered'}>
                                                {messageStatus.icon}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <div ref={chatMessagesEndRef} />
                                </>
                              )}
                            </div>

                            <div className="chat-input-area-modern">
                              {selectedFiles.length > 0 && (
                                <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                                  {selectedFiles.map((file, index) => (
                                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                                      <span>{file.name}</span>
                                      <button
                                        type="button"
                                        onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  multiple
                                  accept="image/*,.pdf,.doc,.docx,.txt"
                                  onChange={handleFileSelect}
                                  style={{ display: 'none' }}
                                  id="chat-file-input-teacher-group"
                                />
                                <label
                                  htmlFor="chat-file-input-teacher-group"
                                  style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                                  title="Attach file"
                                >
                                  📎
                                </label>
                                <input
                                  type="text"
                                  value={newMessage}
                                  onChange={(e) => setNewMessage(e.target.value)}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      sendGroupMessage();
                                    }
                                  }}
                                  placeholder="Type your message..."
                                  className="chat-input-modern"
                                  style={{ flex: 1, minWidth: 0, width: '100%' }}
                                />
                                <button
                                  type="button"
                                  onClick={sendGroupMessage}
                                  disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                                  className="chat-send-button"
                                  title="Send message"
                                >
                                  {uploadingFiles ? '⏳' : '➤'}
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="chat-empty-state">
                        <div className="chat-empty-icon">👥</div>
                        <h3 className="chat-empty-title">Select a Group</h3>
                        <p className="chat-empty-message">Choose a chat group from the list to start chatting</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </main>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="32" fill="#D1FAE5"/>
                <path d="M20 32L28 40L44 24" stroke="#10B981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="success-popup-title">Attendance Saved Successfully!</h3>
            <p className="success-popup-message">
              Attendance has been saved for <strong>{savedStudentsCount}</strong> {savedStudentsCount === 1 ? 'student' : 'students'}
            </p>
            <button 
              className="success-popup-button"
              onClick={() => setShowSuccessPopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Total Marks Applied Popup */}
      {showTotalMarksPopup && (
        <div className="total-marks-popup-overlay" onClick={() => setShowTotalMarksPopup(false)}>
          <div className="total-marks-popup" onClick={(e) => e.stopPropagation()}>
            <div className="total-marks-popup-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="40" fill="#FFF7ED"/>
                <path d="M25 40L35 50L55 30" stroke="#FF8B3D" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="40" cy="40" r="35" stroke="#FF8B3D" strokeWidth="2" strokeDasharray="4 4" opacity="0.3"/>
              </svg>
            </div>
            <h3 className="total-marks-popup-title">✓ Total Marks Applied!</h3>
            <p className="total-marks-popup-message">
              Total marks of <strong className="total-marks-highlight">{totalMarksApplied}</strong> has been applied to all students.
            </p>
            <p className="total-marks-popup-hint">
              You can still override individual students if needed.
            </p>
            <button 
              className="total-marks-popup-button"
              onClick={() => setShowTotalMarksPopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Marks Saved Success Popup */}
      {showMarksSavedPopup && (
        <div className="marks-saved-popup-overlay" onClick={() => setShowMarksSavedPopup(false)}>
          <div className="marks-saved-popup" onClick={(e) => e.stopPropagation()}>
            <div className="marks-saved-popup-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="40" fill="#FFF7ED"/>
                <path d="M25 40L35 50L55 30" stroke="#FF8B3D" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="40" cy="40" r="35" stroke="#FF8B3D" strokeWidth="2" strokeDasharray="4 4" opacity="0.3"/>
              </svg>
            </div>
            <h3 className="marks-saved-popup-title">🎉 Marks Saved Successfully!</h3>
            <p className="marks-saved-popup-message">
              Marks and grades have been saved for <strong className="marks-saved-highlight">{marksSavedCount}</strong> {marksSavedCount === 1 ? 'student' : 'students'}.
            </p>
            <p className="marks-saved-popup-hint">
              You can view and edit these marks anytime.
            </p>
            <button 
              className="marks-saved-popup-button"
              onClick={() => setShowMarksSavedPopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Homework Completion Modal */}
      {showHomeworkCompletionModal && selectedHomeworkForCompletion && (
        <div className="homework-completion-modal-overlay" onClick={handleCloseCompletionModal}>
          <div className="homework-completion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="homework-completion-modal-header">
              <h2>📝 Mark Homework Completion</h2>
              <button 
                className="close-modal-btn"
                onClick={handleCloseCompletionModal}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="homework-completion-modal-content">
              <div className="homework-info-section">
                <h3>{selectedHomeworkForCompletion.title}</h3>
                <p className="homework-class-info">
                  📚 {classNames[selectedHomeworkForCompletion.class_id] || `Class ${selectedHomeworkForCompletion.class_id}`} - {selectedHomeworkForCompletion.section_name}
                </p>
                {selectedHomeworkForCompletion.subject && (
                  <p className="homework-subject-info">
                    📖 {selectedHomeworkForCompletion.subject}
                  </p>
                )}
              </div>

              {homeworkStudentsLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner">⏳</div>
                  <p>Loading students...</p>
                </div>
              ) : homeworkStudents.length === 0 ? (
                <div className="no-students-message">
                  <p>No students found for this class and section.</p>
                </div>
              ) : (
                <>
                  <div className="students-list-header">
                    <h4>Students ({homeworkStudents.length})</h4>
                    <div className="completion-stats">
                      <span className="completed-count">
                        ✅ Completed: {Object.values(homeworkCompletionStatus).filter(Boolean).length}
                      </span>
                      <span className="pending-count">
                        ⏳ Pending: {Object.values(homeworkCompletionStatus).filter(v => !v).length}
                      </span>
                    </div>
                  </div>

                  <div className="students-completion-list">
                    {homeworkStudents.map(student => (
                      <div 
                        key={student.id} 
                        className={`student-completion-item ${homeworkCompletionStatus[student.id] ? 'completed' : ''}`}
                      >
                        <div className="student-info">
                          <div className="student-avatar">
                            {student.first_name?.[0]?.toUpperCase() || '👤'}
                          </div>
                          <div className="student-details">
                            <span className="student-name">
                              {student.first_name} {student.last_name}
                            </span>
                            {student.roll_number && (
                              <span className="student-roll">Roll: {student.roll_number}</span>
                            )}
                          </div>
                        </div>
                        <label className="completion-checkbox-label">
                          <input
                            type="checkbox"
                            checked={homeworkCompletionStatus[student.id] || false}
                            onChange={() => handleToggleCompletion(student.id)}
                            className="completion-checkbox"
                          />
                          <span className="checkmark">
                            {homeworkCompletionStatus[student.id] ? '✓' : ''}
                          </span>
                          <span className="completion-text">
                            {homeworkCompletionStatus[student.id] ? 'Completed' : 'Not Completed'}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="homework-completion-modal-actions">
                    <button
                      className="cancel-completion-btn"
                      onClick={handleCloseCompletionModal}
                      disabled={savingCompletion}
                    >
                      Cancel
                    </button>
                    <button
                      className="save-completion-btn"
                      onClick={handleSaveCompletion}
                      disabled={savingCompletion}
                    >
                      {savingCompletion ? '⏳ Saving...' : '💾 Save Completion Status'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Success/Error Popup */}
      {showCompletionSuccessPopup && (
        <div className="completion-success-popup-overlay">
          <div className={`completion-success-popup ${isCompletionError ? 'error-popup' : ''}`}>
            <div className="completion-success-icon">
              {isCompletionError ? (
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="32" fill="#EF4444" opacity="0.1"/>
                  <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20S20.954 12 32 12s20 8.954 20 20-8.954 20-20 20z" fill="#EF4444"/>
                  <path d="M32 24v12M32 40h.01" stroke="#EF4444" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="32" cy="32" r="32" fill="#10B981" opacity="0.1"/>
                  <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20S20.954 12 32 12s20 8.954 20 20-8.954 20-20 20z" fill="#10B981"/>
                  <path d="M26 32l6 6 12-12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 className="completion-success-title">{isCompletionError ? 'Error' : 'Success!'}</h3>
            <p className="completion-success-message">{completionSuccessMessage}</p>
            <button 
              className={`completion-success-close-btn ${isCompletionError ? 'error-btn' : ''}`}
              onClick={() => setShowCompletionSuccessPopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherDashboard;


